import axios from "axios";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useParams, useNavigate } from "react-router-dom";
import EFTAgreementModal from "./EFTAgreementModal";
import InvestmentCalculatorModal from "./InvestmentCalculatorModal";
import LottieSpinner from "../components/Route/LottieSpinner";
import Footer from "../siteComponents/Footer";
import TopBar from "../siteComponents/Topbar";
import { formatInvestorPerkAmount } from "../utils/normalizeInvestorPerks";

import "react-toastify/dist/ReactToastify.css";

import { notifyUser } from "../utils/notifyUser";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

const PitchPdf = ({ src, title }) => {
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [pageCount, setPageCount] = useState(0);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;

        const updateWidth = () => setContainerWidth(container.clientWidth);
        updateWidth();

        const observer = new ResizeObserver(updateWidth);
        observer.observe(container);

        return () => observer.disconnect();
    }, []);

    return (
        <div ref={containerRef} className="w-full bg-white" aria-label={title}>
            <Document
                file={src}
                onLoadSuccess={({ numPages }) => setPageCount(numPages)}
                loading={
                    <div className="py-8 text-center text-sm text-navy">
                        Loading pitch document...
                    </div>
                }
                error={
                    <a
                        href={src}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-6 text-center font-semibold text-navy underline"
                    >
                        Open pitch document
                    </a>
                }
            >
                {containerWidth > 0 &&
                    Array.from({ length: pageCount }, (_, pageIndex) => (
                        <Page
                            key={pageIndex + 1}
                            pageNumber={pageIndex + 1}
                            width={containerWidth}
                            renderAnnotationLayer={false}
                            renderTextLayer={false}
                        />
                    ))}
            </Document>
        </div>
    );
};

PitchPdf.propTypes = {
    src: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
};

const normalizeCampaignPerks = (value) => {
    if (Array.isArray(value)) {
        return value.slice(0, 5).map((perk) =>
            typeof perk === "string"
                ? { amount: "", description: perk }
                : {
                    amount: perk?.amount != null ? String(perk.amount) : "",
                    description: perk?.description != null ? String(perk.description) : "",
                },
        ).filter((perk) => perk.amount || perk.description);
    }
    if (typeof value === "string" && value.trim()) {
        try {
            return normalizeCampaignPerks(JSON.parse(value));
        } catch {
            return [{ amount: "", description: value }];
        }
    }
    return [];
};

const getYouTubeEmbedUrl = (value) => {
    if (typeof value !== "string" || !value.trim()) return "";

    try {
        const input = value.trim();
        const url = new URL(
            /^https?:\/\//i.test(input) ? input : `https://${input}`,
        );
        const hostname = url.hostname.replace(/^www\./, "").toLowerCase();
        const pathParts = url.pathname.split("/").filter(Boolean);
        let videoId = "";

        if (hostname === "youtu.be") {
            videoId = pathParts[0] || "";
        } else if (
            hostname === "youtube.com" ||
            hostname === "m.youtube.com" ||
            hostname === "youtube-nocookie.com"
        ) {
            if (url.pathname === "/watch") {
                videoId = url.searchParams.get("v") || "";
            } else if (
                ["embed", "shorts", "live", "v"].includes(pathParts[0])
            ) {
                videoId = pathParts[1] || "";
            }

            const playlistId = url.searchParams.get("list");
            if (!videoId && playlistId) {
                return `https://www.youtube.com/embed/videoseries?list=${encodeURIComponent(playlistId)}`;
            }

            if (!videoId && pathParts[0] === "channel" && /^UC[\w-]+$/.test(pathParts[1] || "")) {
                const uploadsPlaylistId = `UU${pathParts[1].slice(2)}`;
                return `https://www.youtube.com/embed/videoseries?list=${encodeURIComponent(uploadsPlaylistId)}`;
            }
        }

        return /^[\w-]{11}$/.test(videoId)
            ? `https://www.youtube.com/embed/${videoId}`
            : "";
    } catch {
        return "";
    }
};

function CampaignViewPage({ user }) {
    const normalizeAssetUrl = (url) => {
        if (!url || typeof url !== "string") return "";

        const backendUrl = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
        const normalizedUrl = url.replace(/\\/g, "/");

        if (/^https?:\/\//i.test(normalizedUrl)) {
            const parsedUrl = new URL(normalizedUrl);

            // Uploaded files can contain a stale host from the environment that
            // created the campaign. Serve them from this app's backend so PDF.js
            // does not have to make a cross-origin request.
            if (parsedUrl.pathname.startsWith("/uploads/")) {
                return `${backendUrl}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
            }

            return normalizedUrl;
        }

        return `${backendUrl}/uploads/${normalizedUrl.replace(/^\/?uploads\//, "")}`;
    };

    const normalizeExternalUrl = (url) => {
        if (!url || typeof url !== "string") return "";
        return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    };

    const getLinkLabel = (url) => {
        try {
            const parsedUrl = new URL(normalizeExternalUrl(url));
            return parsedUrl.hostname.replace(/^www\./, "");
        } catch {
            return url;
        }
    };

    // Utility function to detect media file type
    const getMediaType = (fileUrl) => {
        if (!fileUrl) return "unknown";
        if (/\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl)) return "image";
        if (/\.mp4$/i.test(fileUrl)) return "video";
        if (/\.pdf$/i.test(fileUrl)) return "pdf";
        return "unknown";
    };

    // Function to render media content based on type
    const renderMedia = (mediaUrl) => {
        const normalizedMediaUrl = normalizeAssetUrl(mediaUrl);
        const mediaType = getMediaType(normalizedMediaUrl);

        switch (mediaType) {
            case "image":
                return (
                    <div className="flex w-full items-center justify-center bg-gray-50">
                        <img
                            src={normalizedMediaUrl}
                            alt="Update media"
                            className="max-h-[640px] w-full object-contain"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = "none";
                            }}
                        />
                    </div>
                );
            case "video":
                return (
                    <div className="flex w-full items-center justify-center bg-black">
                        <video
                            src={normalizedMediaUrl}
                            controls
                            className="max-h-[640px] w-full object-contain"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = "none";
                            }}
                        />
                    </div>
                );
            case "pdf":
                return (
                    <div className="h-[70vh] min-h-[520px] w-full">
                        <iframe
                            src={normalizedMediaUrl}
                            title="PDF document"
                            width="100%"
                            height="100%"
                            className="border rounded"
                        />
                    </div>
                );
            default:
                return (
                    <div className="flex justify-center items-center h-48 w-full border rounded p-4 bg-gray-50">
                        <div className="text-red-600 font-semibold text-center">
                            Unsupported file type
                        </div>
                    </div>
                );
        }
    };

    const getCampaignDeadline = (campaignData) => {
        if(campaignData?.end_date) return dayjs(campaignData.end_date).endOf("day");
        if(campaignData?.approved_at) return dayjs(campaignData.approved_at).add(1, "year");
        return null;
    };

    // A persisted terminal status is authoritative. The date check keeps the
    // investment action closed if the scheduled status sync has not run yet.
    const isCampaignEnded = (campaignData) => {
        if(["closed", "ended", "completed"].includes(campaignData?.status)) return true;

        const deadline = getCampaignDeadline(campaignData);
        if(!deadline) return false;

        try {
            return deadline.isValid() && dayjs().isAfter(deadline);
        } catch (error) {
            console.error("Error checking campaign end date:", error);
            return false;
        }
    };

    const { campaignId } = useParams();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState({ founders: [], socialLinks: {} });
    const [loading, setLoading] = useState(true);
    const [, setUserRole] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [activePdf, setActivePdf] = useState(null);
    const [activeImage, setActiveImage] = useState(null);
    const [totalInvestment, setTotalInvestment] = useState(0);
    const [campaignQuestions, setCampaignQuestions] = useState([]);
    const [newQuestion, setNewQuestion] = useState("");
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [replyText, setReplyText] = useState("");
    const [selectedQuestionId, setSelectedQuestionId] = useState(null);
    // activeReplyKey: 'thread' = bottom-of-thread reply, or a reply.id = reply-to-that-reply
    const [activeReplyKey, setActiveReplyKey] = useState(null);
    const [submittingReply, setSubmittingReply] = useState(false);
    // expandedReplies: Set of reply IDs whose children are currently visible
    const [expandedReplies, setExpandedReplies] = useState(new Set());
    const [investmentCalculatorModalOpen, setInvestmentCalculatorModalOpen] =
        useState(false);
    const [investorData, setInvestorData] = useState(null);
    const [eftModalOpen, setEftModalOpen] = useState(false);
    const [pendingInvestAmount, setPendingInvestAmount] = useState(null);
    const [agreementAcceptanceToken, setAgreementAcceptanceToken] =
        useState(null);
    const [perksPanelOpen, setPerksPanelOpen] = useState(false);
    const [showAllPerks, setShowAllPerks] = useState(false);
    const perksTriggerRef = useRef(null);
    const investmentTermsRef = useRef(null);
    const perksDialogRef = useRef(null);
    const closePerksPopup = () => {
        setPerksPanelOpen(false);
        setShowAllPerks(false);
        perksTriggerRef.current?.focus();
    };
    useEffect(() => {
        if (!perksPanelOpen) return;
        const dialog = perksDialogRef.current;
        const terms = investmentTermsRef.current;
        if (!dialog || !terms) return;
        const previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const positionDialog = () => {
            const rect = terms.getBoundingClientRect();
            const width = Math.min(448, window.innerWidth - 24);
            const top = Math.max(12, Math.min(rect.top, window.innerHeight - 120));
            dialog.style.width = `${width}px`;
            dialog.style.left = `${window.innerWidth - width - 12}px`;
            dialog.style.top = `${top}px`;
            dialog.style.maxHeight = `${window.innerHeight - top - 12}px`;
        };
        positionDialog();
        dialog.showModal();
        window.addEventListener("resize", positionDialog);
        window.addEventListener("scroll", positionDialog, true);
        return () => {
            window.removeEventListener("resize", positionDialog);
            window.removeEventListener("scroll", positionDialog, true);
            document.body.style.overflow = previousBodyOverflow;
            if (dialog.open) dialog.close();
        };
    }, [perksPanelOpen]);
    useEffect(() => {
        const fetchTotalInvestment = async () => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/investments/total`,
                );
                const data = await res.json();
                setTotalInvestment(data.totalInvestment);
            } catch (error) {
                console.error("Failed to fetch total investment:", error);
            }
        };

        fetchTotalInvestment();
    }, [campaignId]);
    // const {
    //     socialLinks = {
    //         facebook: "",
    //         linkedin: "",
    //         twitter: "",
    //         tiktok: "",
    //         youtube: "",
    //         instagram: "",
    //     },
    // } = campaign;

    // const {
    //     campaignName = "",
    //     campaignDescription = "",
    //     raisedAmount = "0.00",
    //     goalAmount = "0.00",
    //     investorsCount = 0,
    // } = campaign || {};

    useEffect(() => {
        const fetchCampaign = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/view/${campaignId}`,
                );

                // Process legal_uploads if it's a string
                if (
                    res.data.legal_uploads &&
                    typeof res.data.legal_uploads === "string"
                ) {
                    try {
                        res.data.legal_uploads = JSON.parse(
                            res.data.legal_uploads,
                        );
                    } catch (e) {
                        console.error("Error parsing legal_uploads:", e);
                        res.data.legal_uploads = { form_c: [] };
                    }
                } else if (!res.data.legal_uploads) {
                    res.data.legal_uploads = { form_c: [] };
                }

                // Ensure form_c exists and is an array
                if (!res.data.legal_uploads.form_c) {
                    res.data.legal_uploads.form_c = [];
                } else if (!Array.isArray(res.data.legal_uploads.form_c)) {
                    res.data.legal_uploads.form_c = [
                        res.data.legal_uploads.form_c,
                    ];
                }

                // Transform the form_c array to ensure each item has the expected structure
                res.data.legal_uploads.form_c = res.data.legal_uploads.form_c
                    .map((item, index) => {
                        // If item is a string (just a path), convert to object with file property
                        if (typeof item === "string") {
                            return {
                                file: item.startsWith("http")
                                    ? item
                                    : `${process.env.REACT_APP_BACKEND_URL}/uploads/${item.replace(/^\/?uploads\//, "")}`,
                                name: `Document ${index + 1}`,
                            };
                        }
                        // If item is an object but doesn't have file property properly formatted
                        if (typeof item === "object" && item !== null) {
                            if (!item.file && item.path) {
                                item.file = item.path.startsWith("http")
                                    ? item.path
                                    : `${process.env.REACT_APP_BACKEND_URL}/uploads/${item.path.replace(/^\/?uploads\//, "")}`;
                            } else if (
                                item.file &&
                                typeof item.file === "string" &&
                                !item.file.startsWith("http")
                            ) {
                                item.file = `${process.env.REACT_APP_BACKEND_URL}/uploads/${item.file.replace(/^\/?uploads\//, "")}`;
                            }

                            if (!item.name) {
                                item.name = `Document ${index + 1}`;
                            }

                            return item;
                        }
                        // Default fallback for invalid items
                        return null;
                    })
                    .filter((item) => item !== null);

                // Normalize subscription_agreement the same way as form_c
                const rawSa = res.data.legal_uploads.subscription_agreement;
                const saArray = !rawSa
                    ? []
                    : Array.isArray(rawSa)
                      ? rawSa
                      : [rawSa];

                res.data.legal_uploads.subscription_agreement = saArray
                    .map((item, index) => {
                        if (typeof item === "string") {
                            return {
                                file: item.startsWith("http")
                                    ? item
                                    : `${process.env.REACT_APP_BACKEND_URL}/uploads/${item.replace(/^\/?uploads\//, "")}`,
                                name: `Document ${index + 1}`,
                            };
                        }
                        if (typeof item === "object" && item !== null) {
                            if (!item.file && item.path) {
                                item.file = item.path.startsWith("http")
                                    ? item.path
                                    : `${process.env.REACT_APP_BACKEND_URL}/uploads/${item.path.replace(/^\/?uploads\//, "")}`;
                            } else if (
                                item.file &&
                                typeof item.file === "string" &&
                                !item.file.startsWith("http")
                            ) {
                                item.file = `${process.env.REACT_APP_BACKEND_URL}/uploads/${item.file.replace(/^\/?uploads\//, "")}`;
                            }
                            if (!item.name) {
                                item.name = `Document ${index + 1}`;
                            }
                            return item;
                        }
                        return null;
                    })
                    .filter((item) => item !== null);

                const rawFinancials = res.data.legal_uploads.financials;
                const financialsArray = !rawFinancials
                    ? []
                    : Array.isArray(rawFinancials)
                      ? rawFinancials
                      : [rawFinancials];
                res.data.legal_uploads.financials = financialsArray
                    .map((item, index) => {
                        const rawPath = typeof item === "string" ? item : item?.file || item?.path;
                        if (!rawPath) return null;
                        return {
                            ...(typeof item === "object" ? item : {}),
                            file: normalizeAssetUrl(rawPath),
                            name: item?.name || rawPath.split(/[\\/]/).pop() || `Financials ${index + 1}`,
                        };
                    })
                    .filter(Boolean);

                res.data.legal_uploads.risks = Array.isArray(res.data.legal_uploads.risks)
                    ? res.data.legal_uploads.risks.filter((risk) => typeof risk === "string" && risk.trim()).slice(0, 10)
                    : [];

                // Map fundRaisingGoal to fund_raising_goal for frontend compatibility
                if (res.data.fundRaisingGoal !== undefined) {
                    res.data.fund_raising_goal = res.data.fundRaisingGoal;
                }
                res.data.socialLinks = {
                    ...(res.data.socialLinks || {}),
                    website:
                        res.data.socialLinks?.website ||
                        res.data.website_link ||
                        "",
                    facebook:
                        res.data.socialLinks?.facebook ||
                        res.data.facebook_link ||
                        "",
                    linkedin:
                        res.data.socialLinks?.linkedin ||
                        res.data.linkedin_link ||
                        "",
                    twitter:
                        res.data.socialLinks?.twitter ||
                        res.data.twitter_link ||
                        "",
                    tiktok:
                        res.data.socialLinks?.tiktok ||
                        res.data.tiktok_link ||
                        "",
                    youtube:
                        res.data.socialLinks?.youtube ||
                        res.data.youtube_link ||
                        "",
                    instagram:
                        res.data.socialLinks?.instagram ||
                        res.data.instagram_link ||
                        "",
                };
                setCampaign(res.data);
            } catch (error) {
                console.error("Error fetching campaign:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCampaign();
    }, [campaignId]);

    useEffect(() => {
        if (user?.role === "investor") {
            setUserRole("investor");
        }
    }, [user]);

    // Handle hash fragment navigation
    useEffect(() => {
        const rawHash = window.location.hash.substring(1);
        const tabs = [
            "ABOUT",
            "PITCH",
            "TEAM",
            "UPDATES",
            "SOCIALS",
            "LEGAL & FINANCIALS",
            "Q&A",
        ];

        let normalizedHash = "";
        try {
            normalizedHash = decodeURIComponent(rawHash || "").trim().toLowerCase();
        } catch {
            normalizedHash = (rawHash || "").trim().toLowerCase();
        }

        const normalizedTabs = tabs.map((tab) => tab.toLowerCase());
        const tabIndex = tabs.findIndex((tab, idx) => {
            const equivalentValues = [
                tab.toLowerCase(),
                tab.toLowerCase().replace(/\s*&\s*/g, " and "),
                tab.toLowerCase().replace(/\s*&\s*/g, " "),
                tab.toLowerCase().replace(/\s+/g, ""),
                tab.toLowerCase().split(" ").join(""),
            ];

            return (
                normalizedHash === normalizedTabs[idx] ||
                equivalentValues.some((value) => normalizedHash === value) ||
                normalizedHash === "legal" ||
                normalizedHash === "legals" ||
                normalizedHash === "financials" ||
                normalizedHash === "legal and financials" ||
                normalizedHash === "legal financials" ||
                normalizedHash === "legal & financials"
            );
        });

        if (tabIndex !== -1) {
            setActiveTab(tabIndex);
        }
    }, [campaignId]);
    useEffect(() => {
        // Chat tab is temporarily removed
        // if (activeTab === 6) {
        //     axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/chats`)
        //         .then(res => setCampaignChats(res.data))
        //         .catch(err => console.error('Error fetching campaign chats:', err));
        // }

        if (activeTab === 6) {
            setLoadingQuestions(true);
            axios
                .get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/questions`,
                )
                .then(async (res) => {
                    // If we have questions, fetch replies for each question
                    if (Array.isArray(res.data) && res.data.length > 0) {
                        const questionsWithReplies = await Promise.all(
                            res.data.map(async (question) => {
                                try {
                                    const repliesUrl = `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/questions/${question.id}/replies`;
                                    const repliesRes =
                                        await axios.get(repliesUrl);

                                    // Add replies to the question object
                                    return {
                                        ...question,
                                        replies: Array.isArray(repliesRes.data)
                                            ? repliesRes.data
                                            : [],
                                    };
                                } catch (err) {
                                    console.error(
                                        `Error fetching replies for question ${question.id}:`,
                                        err,
                                    );
                                    return {
                                        ...question,
                                        replies: [],
                                    };
                                }
                            }),
                        );

                        setCampaignQuestions(questionsWithReplies);
                    } else {
                        setCampaignQuestions(res.data);
                    }
                    setLoadingQuestions(false);
                })
                .catch((err) => {
                    console.error("Error fetching campaign questions:", err);
                    setLoadingQuestions(false);
                });
        }
    }, [activeTab, campaignId]);

    if (loading) return <div className="p-6 text-center">Loading...</div>;
    if (!campaign)
        return <div className="p-6 text-center">Campaign not found</div>;

    const { founders = [] } = campaign;
    const campaignPerks = normalizeCampaignPerks(
        campaign.legal_uploads?.investor_perks || campaign.investor_perks,
    );

    const handleInvestNowClick = async () => {
        try {
            // Check investor approval status before proceeding
            const statusRes = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/investor-info`,
                { params: { email: user?.email }, withCredentials: true },
            );
            const investorStatus = statusRes.data?.status;

            if (investorStatus !== "approved") {
                notifyUser(null, "error", `Your account status is currently "${investorStatus}". Only approved accounts can proceed with investing.`, "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }

            // Fetch investor data including investment limits
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/investors/${user?.id}/investment-limits`,
                {
                    withCredentials: true,
                },
            );
            setInvestorData(response.data);
            setInvestmentCalculatorModalOpen(true);
        } catch (err) {
            console.error("Error fetching investor data:", err);
            notifyUser(null, "error", "Failed to load your investment limits. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const handleMobileInvestClick = () => {
        if (!user) {
            localStorage.setItem(
                "redirectAfterLogin",
                window.location.pathname,
            );
            localStorage.setItem("redirectSource", "campaign");
            navigate("/login");
            return;
        }

        handleInvestNowClick();
    };

    const handleCalculatorProceed = (calculatorData) => {
        setPendingInvestAmount(calculatorData?.investmentAmount);
        setAgreementAcceptanceToken(null);
        setEftModalOpen(true);
    };

    const handleSubscriptionAgreementOpen = async() => {
        const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/investor/subscription-agreement/opened`,
            { campaignId: Number(campaignId) },
            { withCredentials: true },
        );

        const token = response.data?.acceptanceToken;
        if(!token) {
            throw new Error("Unable to record that the Subscription Agreement was opened.");
        }

        setAgreementAcceptanceToken(token);
        return { acceptanceToken: token };
    };

    const handleEFTAgree = async({ signatureName }) => {
        if(!agreementAcceptanceToken) {
            throw new Error("Open the Subscription Agreement before selecting I Agree.");
        }

        await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/investor/subscription-agreement/accept`,
            {
                campaignId: Number(campaignId),
                acceptanceToken: agreementAcceptanceToken,
                signatureText: signatureName,
            },
            { withCredentials: true },
        );

        setEftModalOpen(false);
        if (pendingInvestAmount) {
            navigate(
                `/invest?amount=${pendingInvestAmount}&campaignId=${campaignId}&acceptanceToken=${encodeURIComponent(agreementAcceptanceToken)}`,
            );
            setPendingInvestAmount(null);
        }
    };

    const handleAskQuestion = async (e) => {
        e.preventDefault();

        if (!newQuestion.trim()) {
            notifyUser(null, "error", "Please enter a question", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/questions`,
                {
                    campaign_id: campaignId,
                    question: newQuestion,
                    user_id: user?.id || null,
                },
            );

            // Add the new question to the list
            setCampaignQuestions([response.data, ...campaignQuestions]);
            setNewQuestion("");
            notifyUser(null, "success", "Question submitted successfully!", "toast", null, null, null, null, null, false, false, "top-center");
        } catch (err) {
            console.error("Error submitting question:", err);
            notifyUser(null, "error", "Failed to submit question. Please try again later.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Function to handle submitting a reply to a question or answer
    const handleReplySubmit = async (e) => {
        e.preventDefault();

        if (!replyText.trim() || !selectedQuestionId) {
            notifyUser(null, "error", "Please enter a reply", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        try {
            setSubmittingReply(true);
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/questions/${selectedQuestionId}/replies`;

            const response = await axios.post(url, {
                question: replyText,
                user_id: user?.id || null,
                reply_to_id:
                    activeReplyKey !== "thread" ? activeReplyKey : null,
            });

            // Update the questions list with the new reply
            setCampaignQuestions((prevQuestions) =>
                prevQuestions.map((q) => {
                    if (q.id === selectedQuestionId) {
                        // Create replies array if it doesn't exist
                        const replies = Array.isArray(q.replies)
                            ? [...q.replies]
                            : [];
                        return {
                            ...q,
                            replies: [...replies, response.data],
                        };
                    }
                    return q;
                }),
            );

            // Auto-expand the parent reply so the new reply is visible
            const repliedToKey = activeReplyKey;

            // Reset form
            setReplyText("");
            setSelectedQuestionId(null);
            setActiveReplyKey(null);

            if (repliedToKey && repliedToKey !== "thread") {
                setExpandedReplies((prev) => {
                    const next = new Set(prev);
                    next.add(repliedToKey);
                    return next;
                });
            }

            // Show a success message
            notifyUser(null, "success", "Your reply has been submitted successfully!", "toast", null, null, null, null, null, false, false, "top-center");
        } catch (err) {
            console.error("Error submitting reply:", err);
            notifyUser(null, "error", "Failed to submit reply. Please try again later.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setSubmittingReply(false);
        }
    };

    // Opens/toggles inline reply form.
    // key = 'thread' for a general thread reply, or a reply.id to reply to a specific reply.
    // mention = first name of the person being replied to (auto-prefixed as @name in the textarea)
    const openReply = (questionId, key, mention) => {
        if (selectedQuestionId === questionId && activeReplyKey === key) {
            setSelectedQuestionId(null);
            setActiveReplyKey(null);
            setReplyText("");
        } else {
            setSelectedQuestionId(questionId);
            setActiveReplyKey(key);
            setReplyText(mention ? `@${mention} ` : "");
        }
    };

    // Recursively renders replies in a tree.
    // parentReplyId=null means top-level replies (directly under the question).
    // parentReplyId=<id> means replies that replied to that specific reply.
    const toggleReplyExpand = (replyId) => {
        setExpandedReplies((prev) => {
            const next = new Set(prev);
            if (next.has(replyId)) next.delete(replyId);
            else next.add(replyId);
            return next;
        });
    };

    const renderRepliesTree = (allReplies, question, parentReplyId = null) => {
        const levelReplies = allReplies.filter((r) => {
            if (parentReplyId === null) {
                // Top-level: reply_to_id is null OR points to something not in this reply list
                const pointsToAReply =
                    r.reply_to_id &&
                    allReplies.some((pr) => pr.id === r.reply_to_id);
                return !pointsToAReply;
            }
            return r.reply_to_id === parentReplyId;
        });

        return levelReplies.map((reply) => {
            const replyIsAdmin = reply.role === "admin";
            const replyIsOwner =
                reply.role === "campaign_owner" ||
                reply.role === "entrepreneur";
            const replyIsPrivileged = replyIsAdmin || replyIsOwner;
            const isReplyingToThis =
                selectedQuestionId === question.id &&
                activeReplyKey === reply.id;
            const replyFirstName = reply.asker_first_name || "Anonymous";
            const childReplies = allReplies.filter(
                (r) => r.reply_to_id === reply.id,
            );
            const isExpanded = expandedReplies.has(reply.id);

            return (
                <div key={reply.id} className="flex flex-col gap-2">
                    {/* Reply bubble */}
                    <div className="flex items-start gap-3">
                        {reply.asker_first_name ? (
                            <div
                                className={`rounded-full w-7 h-7 flex items-center justify-center font-bold text-white text-xs flex-shrink-0 ${replyIsPrivileged ? "bg-[#8B1C2D]" : "bg-navy"}`}
                            >
                                {reply.asker_first_name.charAt(0).toUpperCase()}
                            </div>
                        ) : (
                            <div className="rounded-full w-7 h-7 flex items-center justify-center text-xs bg-[#E5F0F7] text-navy flex-shrink-0">
                                <i className="fas fa-user"></i>
                            </div>
                        )}
                        <div className="flex-1 min-w-0 bg-[#F5F7FA] rounded-xl p-3 border border-[#e3eaf3]">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span
                                    className={`font-semibold text-xs ${replyIsPrivileged ? "text-[#8B1C2D]" : "text-navy"}`}
                                >
                                    {reply.asker_first_name
                                        ? `${reply.asker_first_name} ${reply.asker_last_name}`
                                        : "Anonymous"}
                                </span>
                                {replyIsAdmin && (
                                    <span className="bg-[#F7E5E8] text-[#8B1C2D] text-xs font-medium px-1.5 py-0.5 rounded-full">
                                        Admin
                                    </span>
                                )}
                                {!replyIsAdmin && replyIsOwner && (
                                    <span className="bg-[#F7E5E8] text-[#8B1C2D] text-xs font-medium px-1.5 py-0.5 rounded-full">
                                        Campaign Owner
                                    </span>
                                )}
                                <span className="text-xs text-[#6b7a8f]">
                                    ·{" "}
                                    {dayjs(reply.audit_created_on).format(
                                        "MMM D, YYYY",
                                    )}
                                </span>
                            </div>
                            <div
                                className="text-xs sm:text-sm text-gray-700 leading-relaxed"
                                dangerouslySetInnerHTML={{
                                    __html:
                                        reply.question ||
                                        reply.message ||
                                        reply.answer,
                                }}
                            />
                            <button
                                onClick={() =>
                                    openReply(
                                        question.id,
                                        reply.id,
                                        replyFirstName,
                                    )
                                }
                                className={`mt-2 text-xs font-semibold flex items-center gap-1 transition-colors ${isReplyingToThis ? "text-navy" : "text-[#6b7a8f] hover:text-navy"}`}
                            >
                                <i className="fas fa-reply text-[10px]"></i>
                                {isReplyingToThis ? "Cancel" : "Reply"}
                            </button>
                        </div>
                    </div>
                    {/* Inline form — opens directly under this reply */}
                    {isReplyingToThis && (
                        <div className="ml-10 pl-3 border-l-2 border-navy/20">
                            <div className="flex items-start gap-2">
                                <div
                                    className={`rounded-full w-7 h-7 flex items-center justify-center font-bold text-white text-xs flex-shrink-0 ${user?.role === "admin" ? "bg-[#8B1C2D]" : "bg-navy"}`}
                                >
                                    {user?.first_name ? (
                                        user.first_name.charAt(0).toUpperCase()
                                    ) : (
                                        <i className="fas fa-user text-xs"></i>
                                    )}
                                </div>
                                <form
                                    onSubmit={handleReplySubmit}
                                    className="flex-1"
                                >
                                    <textarea
                                        value={replyText}
                                        onChange={(e) =>
                                            setReplyText(e.target.value)
                                        }
                                        className="w-full px-3 py-2 text-sm border-2 border-[#C2D7E2] rounded-xl focus:ring-2 focus:ring-navy focus:border-transparent resize-none bg-white"
                                        rows={2}
                                        placeholder={
                                            user?.role === "admin"
                                                ? `Replying as Admin to @${replyFirstName}…`
                                                : `Replying to @${replyFirstName}…`
                                        }
                                        autoFocus
                                        required
                                    />
                                    <div className="flex items-center justify-between mt-1.5">
                                        {user?.role === "admin" ? (
                                            <span className="text-xs text-[#8B1C2D] font-semibold flex items-center gap-1">
                                                <i className="fas fa-shield-alt"></i>{" "}
                                                Admin
                                            </span>
                                        ) : (
                                            <span />
                                        )}
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    openReply(
                                                        question.id,
                                                        reply.id,
                                                    )
                                                }
                                                className="text-xs text-[#6b7a8f] px-2 py-1 rounded-lg hover:bg-[#e3eaf3] transition-colors font-medium"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={submittingReply}
                                                className="bg-navy text-white text-xs px-3 py-1 rounded-lg hover:bg-[#003A61] transition-colors flex items-center gap-1 font-medium disabled:opacity-60"
                                            >
                                                {submittingReply ? (
                                                    <>
                                                        <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>{" "}
                                                        Posting…
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-paper-plane"></i>{" "}
                                                        Reply
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                    {/* Nested children — collapsed by default, toggle to expand */}
                    {childReplies.length > 0 && (
                        <div className="mt-1">
                            <button
                                onClick={() => toggleReplyExpand(reply.id)}
                                className="flex items-center gap-1 text-xs text-navy font-medium hover:text-[#003A61] transition-colors py-1"
                            >
                                <i
                                    className={`fas fa-chevron-${isExpanded ? "up" : "down"} text-[9px]`}
                                ></i>
                                {isExpanded
                                    ? "Hide replies"
                                    : `${childReplies.length} repl${childReplies.length === 1 ? "y" : "ies"}`}
                            </button>
                            {isExpanded && (
                                <div className="ml-8 pl-3 border-l-2 border-[#e3eaf3] space-y-3 mt-2">
                                    {renderRepliesTree(
                                        allReplies,
                                        question,
                                        reply.id,
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        });
    };

    if (typeof user === "undefined") {
        return <LottieSpinner />;
    }

    const socialCardCount = [
        "facebook",
        "youtube",
        "tiktok",
        "linkedin",
        "instagram",
        "twitter",
    ].filter((platform) => campaign.socialLinks?.[platform]).length;
    const socialCardWidthClass =
        socialCardCount === 1
            ? "md:w-full"
            : socialCardCount === 2 || socialCardCount === 4
              ? "md:w-[calc(50%-0.75rem)]"
              : "md:w-[calc(33.333%-1rem)]";

    return (
        <>
            <TopBar />
            <div className="font-poppins min-h-screen px-2 sm:px-4 lg:px-6 xl:px-8 flex flex-col mt-16 sm:mt-20">
                {/* Top Row: Background Image + Right Column */}
                <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 xl:gap-8 w-full max-w-[1800px] mx-auto">
                    {/* LEFT COLUMN */}
                    <div className="w-full lg:w-2/3 xl:w-7/10 2xl:w-3/4 flex flex-col">
                        {/* Background Image */}
                        <div className="h-64 sm:h-80 md:h-96 lg:h-[400px] xl:h-[450px] 2xl:h-[500px] border border-gray-400 flex items-center justify-center text-center text-sm text-black rounded-lg overflow-hidden">
                            {campaign.campaign_background_image ? (
                                <img
                                    src={campaign.campaign_background_image}
                                    alt="Campaign Background"
                                    className="h-full w-full object-contain"
                                />
                            ) : (
                                <span className="px-4">
                                    CAMPAIGN BACKGROUND NOT AVAILABLE
                                </span>
                            )}
                        </div>
                        {/* Tabs section just below the image */}
                        <div className="flex flex-col sm:flex-row justify-start gap-1 sm:gap-2 mt-2 mb-4 rounded-2xl w-full">
                            {/* Mobile: Three rows of tabs */}
                            <div className="flex sm:hidden w-full flex-col gap-1">
                                <div className="flex gap-1 w-full">
                                    {["ABOUT", "PITCH", "TEAM"].map(
                                        (tab, idx) => (
                                            <button
                                                key={tab}
                                                className={`flex-1 rounded-[6px] border px-2 py-2 text-xs font-semibold ${
                                                    activeTab === idx
                                                        ? "bg-navy text-white"
                                                        : "border-navy text-navy"
                                                }`}
                                                onClick={() =>
                                                    setActiveTab(idx)
                                                }
                                            >
                                                {tab}
                                            </button>
                                        ),
                                    )}
                                </div>
                                <div className="flex gap-1 w-full">
                                    {[
                                        "UPDATES",
                                        "SOCIALS",
                                        "LEGAL & FINANCIALS",
                                    ].map((tab, idx) => (
                                        <button
                                            key={tab}
                                            className={`flex-1 rounded-[6px] border px-2 py-2 text-xs font-semibold ${
                                                activeTab === idx + 3
                                                    ? "bg-navy text-white"
                                                    : "border-navy text-navy"
                                            }`}
                                            onClick={() =>
                                                setActiveTab(idx + 3)
                                            }
                                        >
                                            {tab}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex gap-1 w-full">
                                    {["Q&A"].map((tab, idx) => (
                                        <button
                                            key={tab}
                                            className={`flex-1 rounded-[6px] border px-2 py-2 text-xs font-semibold ${
                                                activeTab === idx + 6
                                                    ? "bg-navy text-white"
                                                    : "border-navy text-navy"
                                            }`}
                                            onClick={() =>
                                                setActiveTab(idx + 6)
                                            }
                                        >
                                            {tab}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Desktop/Tablet: Single row */}
                            <div className="hidden sm:flex gap-2 w-full">
                                {[
                                    "ABOUT",
                                    "PITCH",
                                    "TEAM",
                                    "UPDATES",
                                    "SOCIALS",
                                    "LEGAL & FINANCIALS",
                                    "Q&A",
                                ].map((tab, idx) => (
                                    <button
                                        key={tab}
                                        className={`flex-1 rounded-[6px] border px-4 py-2 text-sm font-semibold md:text-base ${
                                            activeTab === idx
                                                ? "bg-navy text-white"
                                                : "border-navy text-navy"
                                        }`}
                                        onClick={() => setActiveTab(idx)}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {/* Tab Content */}
                        <div>
                            {/* About Section */}
                            {activeTab === 0 && (
                                <div className="bg-white/80 rounded-xl shadow p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 w-full">
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                        <h2 className="text-[#8B1C2D] font-bold text-xl sm:text-2xl">
                                            ABOUT
                                        </h2>
                                        <button
                                            type="button"
                                            onClick={handleMobileInvestClick}
                                            className="sm:hidden whitespace-nowrap rounded-[6px] bg-navy px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-navy-2"
                                        >
                                            Invest Now
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-x-4 sm:gap-x-6 lg:gap-x-8 gap-y-4 sm:gap-y-6">
                                        <div className="flex items-start gap-3">
                                            <span className="mt-1 text-navy text-lg sm:text-xl">
                                                <i className="fas fa-building"></i>
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-xs text-navy font-semibold uppercase tracking-wide">
                                                    Company Name
                                                </div>
                                                <div className="text-base sm:text-lg text-navy font-medium break-words">
                                                    {campaign.company_name ||
                                                        "—"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <span className="mt-1 text-navy text-lg sm:text-xl">
                                                <i className="fas fa-city"></i>
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-xs text-navy font-semibold uppercase tracking-wide">
                                                    Company City
                                                </div>
                                                <div className="text-base sm:text-lg text-navy font-medium break-words">
                                                    {campaign.company_city ||
                                                        "—"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <span className="mt-1 text-navy text-lg sm:text-xl">
                                                <i className="fas fa-map-marker-alt"></i>
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-xs text-navy font-semibold uppercase tracking-wide">
                                                    Company State
                                                </div>
                                                <div className="text-base sm:text-lg text-navy font-medium break-words">
                                                    {campaign.company_state ||
                                                        "—"}
                                                </div>
                                            </div>
                                        </div>
                                        {campaign.investors_info && (
                                            <div className="flex items-start gap-3">
                                                <span className="mt-1 text-navy text-lg sm:text-xl">
                                                    <i className="fas fa-user-check"></i>
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-xs text-navy font-semibold uppercase tracking-wide">
                                                        Lead Investor
                                                    </div>
                                                    <div className="text-base sm:text-lg text-navy font-medium break-words">
                                                        {campaign.investors_info}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex items-start gap-3">
                                            <span className="mt-1 text-navy text-lg sm:text-xl">
                                                <i className="fas fa-info-circle"></i>
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-xs text-navy font-semibold uppercase tracking-wide">
                                                    Overview Pitch
                                                </div>
                                                <div className="whitespace-pre-line text-base font-medium leading-relaxed text-navy break-words sm:text-lg">
                                                    {campaign.overview_pitch ||
                                                        "—"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <span className="mt-1 text-navy text-lg sm:text-xl">
                                                <i className="fas fa-bullhorn"></i>
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-xs text-navy font-semibold uppercase tracking-wide">
                                                    Call To Action
                                                </div>
                                                <div className="text-base sm:text-lg text-navy font-medium break-words">
                                                    {campaign.call_to_action ||
                                                        "—"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Pitch Section */}
                            {activeTab === 1 && (
                                <div className="w-full space-y-6 sm:space-y-8">
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                        <h2 className="text-[#8B1C2D] font-bold text-xl sm:text-2xl">
                                            PITCH
                                        </h2>
                                        <button
                                            type="button"
                                            onClick={handleMobileInvestClick}
                                            className="sm:hidden whitespace-nowrap rounded-[6px] bg-navy px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-navy-2"
                                        >
                                            Invest Now
                                        </button>
                                    </div>
                                    <div className="space-y-6 sm:space-y-8">
                                        <div className="flex items-start gap-3">
                                            <span className="mt-1 text-navy text-lg sm:text-xl">
                                                <i className="fas fa-exclamation-circle"></i>
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-xs text-navy font-semibold uppercase tracking-wide">
                                                    Problem
                                                </div>
                                                <div className="whitespace-pre-line text-sm leading-relaxed text-navy break-words sm:text-base">
                                                    {campaign.pitch_problem ||
                                                        "—"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <span className="mt-1 text-navy text-lg sm:text-xl">
                                                <i className="fas fa-lightbulb"></i>
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-xs text-navy font-semibold uppercase tracking-wide">
                                                    Solution
                                                </div>
                                                <div className="whitespace-pre-line text-sm leading-relaxed text-navy break-words sm:text-base">
                                                    {campaign.pitch_solution ||
                                                        "—"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {Array.isArray(campaign.pitch_slides) &&
                                        campaign.pitch_slides.length > 0 && (
                                            <div className="space-y-4 sm:space-y-6">
                                                {campaign.pitch_slides.map(
                                                    (slide, idx) => (
                                                        <React.Fragment
                                                            key={idx}
                                                        >
                                                            {slide.file && (
                                                                <div className="w-full overflow-hidden rounded-lg bg-white shadow">
                                                                    {slide.file.match(
                                                                        /\.(jpeg|jpg|gif|png|webp)(\?.*)?$/i,
                                                                    ) ? (
                                                                        <img
                                                                            src={normalizeAssetUrl(slide.file)}
                                                                            alt={`Pitch Slide ${
                                                                                idx +
                                                                                1
                                                                            }`}
                                                                            className="max-h-[720px] w-full object-contain"
                                                                        />
                                                                    ) : slide.file.match(
                                                                          /\.pdf(\?.*)?$/i,
                                                                      ) ? (
                                                                        <PitchPdf
                                                                            src={normalizeAssetUrl(slide.file)}
                                                                            title={`Pitch slide ${idx + 1}`}
                                                                        />
                                                                    ) : (
                                                                        <a
                                                                            href={normalizeAssetUrl(slide.file)}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="block p-6 text-center font-semibold text-navy underline"
                                                                        >
                                                                            Open pitch document
                                                                        </a>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {slide.overview && (
                                                                <div className="whitespace-pre-line py-4 text-sm leading-relaxed text-navy sm:py-5 sm:text-base">
                                                                    {slide.overview}
                                                                </div>
                                                            )}
                                                        </React.Fragment>
                                                    ),
                                                )}
                                            </div>
                                        )}
                                </div>
                            )}

                            {/* Team Section */}
                            {activeTab === 2 && (
                                <div className="bg-white/80 rounded-xl shadow p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8 w-full">
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                        <h2 className="text-[#8B1C2D] font-bold text-xl sm:text-2xl">
                                            TEAM
                                        </h2>
                                        <button
                                            type="button"
                                            onClick={handleMobileInvestClick}
                                            className="sm:hidden whitespace-nowrap rounded-[6px] bg-navy px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-navy-2"
                                        >
                                            Invest Now
                                        </button>
                                    </div>
                                    {founders.length === 0 && (
                                        <div className="text-navy italic text-sm sm:text-base">
                                            No founders listed for this
                                            campaign.
                                        </div>
                                    )}
                                    <div className="space-y-4 sm:space-y-6">
                                        {founders.map((founder, idx) => (
                                            <div
                                                key={idx}
                                                className="bg-white rounded-lg shadow p-4 sm:p-6 flex flex-col lg:flex-row lg:items-center lg:gap-6 xl:gap-8"
                                            >
                                                <div className="flex-1 mb-4 lg:mb-0">
                                                    <div className="mb-2">
                                                        <div className="font-display text-lg font-bold text-navy break-words sm:text-xl">
                                                            {founder.name ||
                                                                "—"}
                                                        </div>
                                                    </div>
                                                    {founder.linkedin && (
                                                        <a
                                                            href={normalizeExternalUrl(founder.linkedin)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-[#0077b5] hover:underline"
                                                        >
                                                            <i className="fab fa-linkedin"></i>
                                                            View LinkedIn profile
                                                        </a>
                                                    )}
                                                    <div>
                                                        <span className="text-xs text-navy font-semibold uppercase tracking-wide">
                                                            About
                                                        </span>
                                                        <div className="whitespace-pre-line text-sm leading-relaxed text-navy break-words sm:text-base">
                                                            {founder.about ||
                                                                "—"}
                                                        </div>
                                                    </div>
                                                </div>
                                                {founder.file && (
                                                    <div className="flex flex-col items-center lg:w-1/3 xl:w-1/4 w-full">
                                                        <img
                                                            src={founder.file}
                                                            alt={`${founder.name || "Team member"} headshot`}
                                                            className="h-24 w-24 sm:h-28 sm:w-28 object-cover object-center rounded-lg border border-navy mb-2 cursor-pointer hover:opacity-80 transition-opacity"
                                                            onClick={() => {
                                                                setActiveImage(
                                                                    founder.file,
                                                                );
                                                                setModalOpen(
                                                                    true,
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Updates Section */}
                            {activeTab === 3 && (
                                <div className="bg-white/80 rounded-xl shadow p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 w-full">
                                    <h2 className="text-[#8B1C2D] font-bold text-xl sm:text-2xl mb-4 flex items-center gap-2">
                                        UPDATES
                                    </h2>
                                    <div>
                                        {!campaign.updates ||
                                        campaign.updates.length === 0 ? (
                                            <p className="italic text-navy text-base sm:text-lg">
                                                ANY UPDATES TO THE ACCOUNT WILL
                                                BE LISTED HERE
                                            </p>
                                        ) : (
                                            <ul className="space-y-6">
                                                {campaign.updates.map(
                                                    (update, idx) => {
                                                        // Handle different types of updates
                                                        const isObject =
                                                            typeof update ===
                                                                "object" &&
                                                            update !== null;
                                                        const hasMedia =
                                                            isObject &&
                                                            update.media;
                                                        const title =
                                                            isObject &&
                                                            update.title;
                                                        const url =
                                                            isObject &&
                                                            update.url;
                                                        const updateText =
                                                            isObject
                                                                ? update.description || update.text
                                                                : typeof update === "string"
                                                                  ? update
                                                                  : "";
                                                        const hasText = !!updateText;
                                                        const timestamp =
                                                            isObject &&
                                                            update.timestamp
                                                                ? new Date(
                                                                      update.timestamp,
                                                                  ).toLocaleDateString()
                                                                : null;

                                                        return (
                                                            <li
                                                                key={idx}
                                                                className="bg-white rounded-lg shadow p-4 flex flex-col gap-3 h-auto"
                                                            >
                                                                {/* Display timestamp if available */}
                                                                {timestamp && (
                                                                    <div className="text-xs text-gray-500 mb-1">
                                                                        {
                                                                            timestamp
                                                                        }
                                                                    </div>
                                                                )}

                                                                {title && (
                                                                    <div className="text-base sm:text-lg font-semibold text-navy break-words">
                                                                        {title}
                                                                    </div>
                                                                )}

                                                                {url && (
                                                                    <a
                                                                        href={normalizeExternalUrl(url)}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="inline-flex w-fit max-w-full items-center gap-2 rounded-full border border-navy/20 bg-[#f0f6fb] px-3 py-1 text-sm font-semibold text-navy transition hover:border-navy/40 hover:bg-[#e3eef8]"
                                                                    >
                                                                        <i className="fas fa-external-link-alt text-[10px]"></i>
                                                                        <span className="truncate">
                                                                            {getLinkLabel(url)}
                                                                        </span>
                                                                    </a>
                                                                )}

                                                                {/* Display text content */}
                                                                <div className="whitespace-pre-wrap text-sm leading-relaxed text-navy break-words sm:text-base">
                                                                    {hasText
                                                                        ? updateText
                                                                          : "Update"}
                                                                </div>

                                                                {/* Display media content if available */}
                                                                {hasMedia && (
                                                                    <div className="border rounded bg-white overflow-hidden">
                                                                        {renderMedia(
                                                                            update.media,
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </li>
                                                        );
                                                    },
                                                )}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Social Section */}
                            {/* Social Section */}
                            {activeTab === 4 && (
                                <div className="flex w-full flex-col gap-4 rounded-xl bg-white/80 p-4 shadow sm:gap-6 sm:p-6 lg:p-8">
                                    <div className="contents">
                                        <h2 className="text-[#8B1C2D] font-bold text-lg sm:text-xl mb-4">
                                            SOCIAL MEDIA FEED
                                        </h2>
                                        {campaign.socialLinks.website && (
                                            <a
                                                href={normalizeExternalUrl(campaign.socialLinks.website)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-3 rounded-xl border border-navy/20 bg-white p-4 font-semibold text-navy shadow-sm transition hover:border-navy hover:bg-[#f0f6fb]"
                                            >
                                                <i className="fas fa-globe"></i>
                                                <span className="min-w-0 flex-1 truncate">
                                                    {getLinkLabel(campaign.socialLinks.website)}
                                                </span>
                                                <i className="fas fa-external-link-alt text-xs"></i>
                                            </a>
                                        )}
                                        <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                                        <div className="contents">
                                            {campaign.socialLinks.facebook && (
                                            <div className={`flex h-[320px] w-full flex-col items-center rounded-lg border-2 border-[#3b5998] bg-white p-4 shadow md:flex-none ${socialCardWidthClass}`}>
                                                <span className="mb-2 flex shrink-0 items-center gap-2 text-lg font-bold text-[#3b5998] sm:text-2xl">
                                                    <i className="fab fa-facebook-f"></i>{" "}
                                                    Facebook
                                                </span>
                                                <iframe
                                                    src={`https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(normalizeExternalUrl(campaign.socialLinks.facebook))}&tabs=timeline&width=500&height=460&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=false`}
                                                    title="Facebook campaign feed"
                                                    className="min-h-0 w-full flex-1 rounded-lg border"
                                                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                                                />
                                                <a href={normalizeExternalUrl(campaign.socialLinks.facebook)} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex shrink-0 items-center gap-2 rounded-lg bg-[#3b5998] px-4 py-2 text-sm font-semibold text-white hover:bg-[#30497d]">Open on Facebook <i className="fas fa-external-link-alt text-xs" aria-hidden="true"></i></a>
                                            </div>
                                            )}
                                            {/* YouTube embed */}
                                            {campaign.socialLinks.youtube && (
                                                <div className={`flex h-[320px] w-full flex-col items-center rounded-lg border-2 border-[#ff0000] bg-white p-4 shadow md:flex-none ${socialCardWidthClass}`}>
                                                    <span className="mb-2 flex shrink-0 items-center gap-2 text-lg font-bold text-[#ff0000] sm:text-2xl">
                                                        <i className="fab fa-youtube"></i>{" "}
                                                        YouTube
                                                    </span>
                                                    {(() => {
                                                        const embedUrl =
                                                            getYouTubeEmbedUrl(
                                                                campaign
                                                                    .socialLinks
                                                                    .youtube,
                                                            );
                                                        return embedUrl ? (
                                                            <iframe
                                                                src={embedUrl}
                                                                title="YouTube campaign video"
                                                                className="min-h-0 w-full flex-1 rounded-lg border"
                                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                allowFullScreen
                                                            />
                                                        ) : (
                                                            <div className="flex flex-1 items-center justify-center">
                                                                <p className="text-center text-sm text-gray-600">
                                                                    Watch this campaign video on YouTube.
                                                                </p>
                                                            </div>
                                                        );
                                                    })()}
                                                    <a
                                                        href={normalizeExternalUrl(campaign.socialLinks.youtube)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="mt-3 inline-flex shrink-0 items-center gap-2 rounded-lg bg-[#ff0000] px-4 py-2 text-sm font-semibold text-white hover:bg-[#d90000]"
                                                    >
                                                        Open on YouTube
                                                        <i className="fas fa-external-link-alt text-xs" aria-hidden="true"></i>
                                                    </a>
                                                </div>
                                            )}
                                            {/* TikTok Embed */}
                                            {campaign.socialLinks.tiktok && (
                                                <div className={`flex h-[320px] w-full flex-col items-center rounded-lg border-2 border-black bg-white p-4 shadow md:flex-none ${socialCardWidthClass}`}>
                                                    <span className="mb-2 flex shrink-0 items-center gap-2 text-lg font-bold text-black sm:text-2xl">
                                                        <i className="fab fa-tiktok"></i>{" "}
                                                        TikTok
                                                    </span>
                                                    {(() => {
                                                        const match =
                                                            campaign.socialLinks.tiktok.match(
                                                                /tiktok\.com\/@[^/]+\/video\/(\d+)/i,
                                                            );
                                                        const videoId = match
                                                            ? match[1]
                                                            : null;
                                                        return videoId ? (
                                                            <iframe
                                                                src={`https://www.tiktok.com/player/v1/${videoId}`}
                                                                width="100%"
                                                                style={{
                                                                    flex: 1,
                                                                    minHeight: 0,
                                                                }}
                                                                allowFullScreen
                                                                frameBorder="0"
                                                                title="TikTok Video"
                                                                className="rounded-lg border w-full"
                                                            />
                                                        ) : (
                                                            <div className="flex flex-1 items-center justify-center">
                                                                <p className="text-center text-sm text-gray-600">
                                                                    Watch this campaign video on TikTok.
                                                                </p>
                                                            </div>
                                                        );
                                                    })()}
                                                    <a
                                                        href={normalizeExternalUrl(campaign.socialLinks.tiktok)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="mt-3 inline-flex shrink-0 items-center gap-2 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                                                    >
                                                        Open on TikTok
                                                        <i className="fas fa-external-link-alt text-xs" aria-hidden="true"></i>
                                                    </a>
                                                </div>
                                            )}
                                            {/* If none of the top 4 are present */}
                                            {!campaign.socialLinks.youtube &&
                                                !campaign.socialLinks
                                                    .instagram &&
                                                !campaign.socialLinks.tiktok &&
                                                !campaign.socialLinks.linkedin &&
                                                !campaign.socialLinks.twitter &&
                                                !campaign.socialLinks
                                                    .facebook && (
                                                    <div className="text-navy italic md:col-span-3 text-sm sm:text-base">
                                                        No social media embeds
                                                        provided for this
                                                        campaign.
                                                    </div>
                                                )}
                                        </div>
                                    {(campaign.socialLinks.linkedin ||
                                        campaign.socialLinks.instagram ||
                                        campaign.socialLinks.twitter) && (
                                        <div className="contents">
                                            {campaign.socialLinks.linkedin && (
                                                <div className={`flex h-[320px] w-full flex-col items-center rounded-lg border-2 border-[#0077b5] bg-white p-4 shadow md:flex-none ${socialCardWidthClass}`}>
                                                    <span className="mb-2 flex shrink-0 items-center gap-2 text-lg font-bold text-[#0077b5] sm:text-2xl">
                                                        <i className="fab fa-linkedin-in"></i>{" "}
                                                        LinkedIn
                                                    </span>
                                                    {(() => {
                                                        const linkedInUrl = normalizeExternalUrl(campaign.socialLinks.linkedin);
                                                        const activityId = linkedInUrl.match(/activity-(\d+)/)?.[1];
                                                        const postUrn = linkedInUrl.match(/urn:li:(?:activity|ugcPost|share):\d+/)?.[0];
                                                        const embedUrn = postUrn || (activityId ? `urn:li:activity:${activityId}` : null);
                                                        return embedUrn ? (
                                                            <iframe
                                                                src={`https://www.linkedin.com/embed/feed/update/${embedUrn}`}
                                                                title="LinkedIn campaign post"
                                                                className="min-h-0 w-full flex-1 rounded-lg border"
                                                                allowFullScreen
                                                            />
                                                        ) : (
                                                            <div className="flex flex-1 items-center justify-center">
                                                                <p className="text-center text-sm text-gray-600">
                                                                    Follow this campaign on LinkedIn for news and updates.
                                                                </p>
                                                            </div>
                                                        );
                                                    })()}
                                                    {campaign.socialLinks.linkedin && <a href={normalizeExternalUrl(campaign.socialLinks.linkedin)} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#0077b5] px-4 py-2 text-sm font-semibold text-white hover:bg-[#005f8e]">Open on LinkedIn <i className="fas fa-external-link-alt text-xs" aria-hidden="true"></i></a>}
                                                </div>
                                            )}
                                            {campaign.socialLinks.instagram && (
                                                <div className={`flex h-[320px] w-full flex-col items-center rounded-lg border-2 border-pink-500 bg-white p-4 shadow md:flex-none ${socialCardWidthClass}`}>
                                                    <span className="mb-2 flex shrink-0 items-center gap-2 text-lg font-bold text-pink-500 sm:text-2xl">
                                                        <i className="fab fa-instagram"></i>{" "}
                                                        Instagram
                                                    </span>
                                                    <div className="flex flex-1 items-center justify-center">
                                                        <p className="text-center text-sm text-gray-600">
                                                            See this campaign’s photos and updates on Instagram.
                                                        </p>
                                                    </div>
                                                    <a href={normalizeExternalUrl(campaign.socialLinks.instagram)} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-pink-500 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-600">Open on Instagram <i className="fas fa-external-link-alt text-xs" aria-hidden="true"></i></a>
                                                </div>
                                            )}
                                            {campaign.socialLinks.twitter && (
                                                <div className={`flex h-[320px] w-full flex-col items-center rounded-lg border-2 border-[#1da1f2] bg-white p-4 shadow md:flex-none ${socialCardWidthClass}`}>
                                                    <span className="mb-2 flex shrink-0 items-center gap-2 text-lg font-bold text-[#1da1f2] sm:text-2xl">
                                                        <i className="fab fa-twitter"></i>{" "}
                                                        X / Twitter
                                                    </span>
                                                    <div className="flex flex-1 items-center justify-center">
                                                        <p className="text-center text-sm text-gray-600">
                                                            Follow this campaign on X for news and updates.
                                                        </p>
                                                    </div>
                                                    <a
                                                        href={normalizeExternalUrl(campaign.socialLinks.twitter)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#1da1f2] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1687cf]"
                                                    >
                                                        Open on X
                                                        <i className="fas fa-external-link-alt text-xs" aria-hidden="true"></i>
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    </div>
                                    </div>
                                </div>
                            )}

                            {/* Legal & Financials Section */}
                            {activeTab === 5 && (
                                <div className="w-full space-y-8 rounded-xl bg-white/80 p-4 shadow sm:p-6 lg:p-8">
                                    <h2 className="text-xl font-bold text-[#8B1C2D] sm:text-2xl">LEGAL & FINANCIALS</h2>

                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                        {[
                                            {
                                                label: "Form C",
                                                icon: "fa-landmark",
                                                documents: campaign.legal_uploads?.edgar_filing_url
                                                    ? [{ file: normalizeExternalUrl(campaign.legal_uploads.edgar_filing_url), name: "View EDGAR filing" }]
                                                    : campaign.legal_uploads?.form_c || [],
                                            },
                                            {
                                                label: "Subscription Agreement",
                                                icon: "fa-file-signature",
                                                documents: campaign.legal_uploads?.subscription_agreement || [],
                                            },
                                            {
                                                label: "Financials",
                                                icon: "fa-chart-line",
                                                documents: campaign.legal_uploads?.financials || [],
                                            },
                                        ].map(({ label, icon, documents }) => (
                                            <div key={label} className="rounded-xl border border-navy/15 bg-white p-5 shadow-sm">
                                                <div className="mb-3 flex items-center gap-2 font-semibold text-navy">
                                                    <i className={`fas ${icon}`}></i>
                                                    <span>{label}</span>
                                                </div>
                                                {documents.length ? (
                                                    <div className="space-y-2">
                                                        {documents.map((document, idx) => (
                                                            <a
                                                                key={`${label}-${idx}`}
                                                                href={document.file}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex items-center justify-between gap-3 rounded-lg bg-[#f0f6fb] px-3 py-2 text-sm font-medium text-navy hover:bg-[#e3eef8]"
                                                            >
                                                                <span className="min-w-0 truncate">{document.name || `${label} ${idx + 1}`}</span>
                                                                <i className="fas fa-external-link-alt shrink-0 text-xs"></i>
                                                            </a>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-sm italic text-gray-500">Not available.</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    <section>
                                        <h3 className="mb-4 text-lg font-semibold text-navy sm:text-xl">Risks</h3>
                                        {campaign.legal_uploads?.risks?.length ? (
                                            <ol className="space-y-3 pl-6 text-sm leading-7 text-gray-700 sm:text-base">
                                                {campaign.legal_uploads.risks.slice(0, 10).map((risk, idx) => (
                                                    <li key={idx} className="list-decimal whitespace-pre-wrap pl-2">{risk}</li>
                                                ))}
                                            </ol>
                                        ) : <p className="text-sm italic text-gray-500">No risks have been added.</p>}
                                    </section>

                                    <section>
                                        <h3 className="mb-4 text-lg font-semibold text-navy sm:text-xl">Use of Funds</h3>
                                        <p className="whitespace-pre-wrap text-sm leading-7 text-gray-700 sm:text-base">
                                            {campaign.legal_uploads?.use_of_funds || "No use of funds information has been added."}
                                        </p>
                                    </section>
                                </div>
                            )}

                            {/* Legacy legal card layout retained for historical data compatibility. */}
                            {activeTab === -5 && (
                                <div className="bg-white/80 rounded-xl shadow p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 w-full">
                                    <h2 className="text-[#8B1C2D] font-bold text-xl sm:text-2xl mb-2">
                                        LEGAL & FINANCIALS
                                    </h2>

                                    {/* Form C Documents */}
                                    <div className="mt-4 sm:mt-6">
                                        <h3 className="text-navy font-semibold text-lg sm:text-xl mb-2 flex items-center gap-2">
                                            <i className="fas fa-file-contract"></i>{" "}
                                            Form C Documents
                                        </h3>

                                        {/* Check if legal_uploads and form_c exist and have content */}
                                        {campaign.legal_uploads &&
                                        campaign.legal_uploads.form_c &&
                                        campaign.legal_uploads.form_c.length >
                                            0 ? (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {campaign.legal_uploads.form_c.map(
                                                    (doc, idx) => {
                                                        // Ensure doc.file exists and is a string
                                                        const fileUrl =
                                                            doc &&
                                                            doc.file &&
                                                            typeof doc.file ===
                                                                "string"
                                                                ? doc.file
                                                                : null;
                                                        const docName =
                                                            doc && doc.name
                                                                ? doc.name
                                                                : `Document ${
                                                                      idx + 1
                                                                  }`;

                                                        if (!fileUrl) {
                                                            return (
                                                                <div
                                                                    key={idx}
                                                                    className="relative bg-white rounded-lg shadow-sm p-3 border border-gray-200"
                                                                >
                                                                    <div className="flex flex-col items-center">
                                                                        <div className="h-32 w-full rounded-md border border-gray-300 bg-gray-50 flex flex-col items-center justify-center">
                                                                            <span className="text-4xl text-gray-400 mb-2">
                                                                                <i className="fas fa-exclamation-circle"></i>
                                                                            </span>
                                                                            <span className="text-xs text-gray-500 px-2 text-center">
                                                                                Invalid
                                                                                document
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }

                                                        // Check if it's an image or PDF
                                                        const isImage =
                                                            fileUrl.match(
                                                                /\.(jpeg|jpg|gif|png|webp)$/i,
                                                            );
                                                        const isPdf =
                                                            fileUrl.match(
                                                                /\.pdf$/i,
                                                            );

                                                        return (
                                                            <div
                                                                key={idx}
                                                                className="relative bg-white rounded-lg shadow-sm p-3 border border-gray-200"
                                                            >
                                                                {isImage ? (
                                                                    <img
                                                                        src={
                                                                            fileUrl
                                                                        }
                                                                        alt={`Form C Document ${idx + 1}`}
                                                                        className="h-32 w-full object-cover rounded-md border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                                                                        onClick={() => {
                                                                            setActiveImage(
                                                                                fileUrl,
                                                                            );
                                                                            setModalOpen(
                                                                                true,
                                                                            );
                                                                        }}
                                                                    />
                                                                ) : isPdf ? (
                                                                    <div
                                                                        className="h-32 w-full rounded-md border border-gray-300 bg-gray-50 cursor-pointer flex flex-col items-center justify-center hover:bg-gray-100 transition-colors"
                                                                        onClick={() => {
                                                                            setActivePdf(
                                                                                fileUrl,
                                                                            );
                                                                            setModalOpen(
                                                                                true,
                                                                            );
                                                                        }}
                                                                    >
                                                                        <span className="text-4xl text-[#8B1C2D]">
                                                                            <i className="fas fa-file-pdf"></i>
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <a
                                                                        href={
                                                                            fileUrl
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="h-32 w-full rounded-md border border-gray-300 bg-gray-50 flex flex-col items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
                                                                    >
                                                                        <span className="text-4xl text-gray-500">
                                                                            <i className="fas fa-file-alt"></i>
                                                                        </span>
                                                                        <span className="text-xs text-gray-500 mt-2">
                                                                            Click
                                                                            to
                                                                            view
                                                                        </span>
                                                                    </a>
                                                                )}
                                                            </div>
                                                        );
                                                    },
                                                )}
                                            </div>
                                        ) : (
                                            <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500 italic">
                                                No Form C documents available
                                                for this campaign.
                                            </div>
                                        )}
                                    </div>

                                    {/* Subscription Agreement Documents */}
                                    <div className="mt-4 sm:mt-6">
                                        <h3 className="text-navy font-semibold text-lg sm:text-xl mb-2 flex items-center gap-2">
                                            <i className="fas fa-file-signature"></i>{" "}
                                            Subscription Agreement
                                        </h3>

                                        {campaign.legal_uploads &&
                                        campaign.legal_uploads
                                            .subscription_agreement &&
                                        campaign.legal_uploads
                                            .subscription_agreement.length >
                                            0 ? (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {campaign.legal_uploads.subscription_agreement.map(
                                                    (doc, idx) => {
                                                        const fileUrl =
                                                            doc &&
                                                            doc.file &&
                                                            typeof doc.file ===
                                                                "string"
                                                                ? doc.file
                                                                : null;
                                                        const docName =
                                                            doc && doc.name
                                                                ? doc.name
                                                                : `Document ${idx + 1}`;

                                                        if (!fileUrl) {
                                                            return (
                                                                <div
                                                                    key={idx}
                                                                    className="relative bg-white rounded-lg shadow-sm p-3 border border-gray-200"
                                                                >
                                                                    <div className="h-32 w-full rounded-md border border-gray-300 bg-gray-50 flex flex-col items-center justify-center">
                                                                        <span className="text-4xl text-gray-400 mb-2">
                                                                            <i className="fas fa-exclamation-circle"></i>
                                                                        </span>
                                                                        <span className="text-xs text-gray-500 px-2 text-center">
                                                                            Invalid
                                                                            document
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }

                                                        const isImage =
                                                            fileUrl.match(
                                                                /\.(jpeg|jpg|gif|png|webp)$/i,
                                                            );
                                                        const isPdf =
                                                            fileUrl.match(
                                                                /\.pdf$/i,
                                                            );

                                                        return (
                                                            <div
                                                                key={idx}
                                                                className="relative bg-white rounded-lg shadow-sm p-3 border border-gray-200"
                                                            >
                                                                {isImage ? (
                                                                    <img
                                                                        src={
                                                                            fileUrl
                                                                        }
                                                                        alt={`Subscription Agreement ${idx + 1}`}
                                                                        className="h-32 w-full object-cover rounded-md border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                                                                        onClick={() => {
                                                                            setActiveImage(
                                                                                fileUrl,
                                                                            );
                                                                            setModalOpen(
                                                                                true,
                                                                            );
                                                                        }}
                                                                    />
                                                                ) : isPdf ? (
                                                                    <div
                                                                        className="h-32 w-full rounded-md border border-gray-300 bg-gray-50 cursor-pointer flex flex-col items-center justify-center hover:bg-gray-100 transition-colors"
                                                                        onClick={() => {
                                                                            setActivePdf(
                                                                                fileUrl,
                                                                            );
                                                                            setModalOpen(
                                                                                true,
                                                                            );
                                                                        }}
                                                                    >
                                                                        <span className="text-4xl text-[#8B1C2D]">
                                                                            <i className="fas fa-file-pdf"></i>
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <a
                                                                        href={
                                                                            fileUrl
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="h-32 w-full rounded-md border border-gray-300 bg-gray-50 flex flex-col items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
                                                                    >
                                                                        <span className="text-4xl text-gray-500">
                                                                            <i className="fas fa-file-alt"></i>
                                                                        </span>
                                                                        <span className="text-xs text-gray-500 mt-2">
                                                                            Click
                                                                            to
                                                                            view
                                                                        </span>
                                                                    </a>
                                                                )}
                                                            </div>
                                                        );
                                                    },
                                                )}
                                            </div>
                                        ) : (
                                            <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500 italic">
                                                No Subscription Agreement
                                                documents available for this
                                                campaign.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Chat Section - Temporarily hidden
                            {activeTab === 7 && (
                                <div className="bg-white/80 rounded-xl shadow p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 w-full">
                                <div className="space-y-6 sm:space-y-8">
                                    <h2 className="text-[#8B1C2D] font-bold text-xl sm:text-2xl mb-6">INVESTOR CONVERSATIONS</h2>
                                    {campaignChats.length === 0 ? (
                                        <div className="text-navy italic text-sm sm:text-base">No chats found for this campaign.</div>
                                    ) : (
                                        campaignChats.map((chat, idx) => {
                                            const investorName = `${chat.investor.first_name} ${chat.investor.last_name}`;
                                            const investorInitial = investorName.charAt(0).toUpperCase();
                                            return (
                                                <div key={chat.investor.id} className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
                                                    <div className="flex items-center gap-3 sm:gap-4 mb-4">
                                                        <div className="rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center font-bold text-white text-lg sm:text-xl bg-navy">
                                                            {investorInitial}
                                                        </div>
                                                        <div>
                                                            <div className="font-bold text-base sm:text-lg text-navy">{investorName}</div>
                                                        </div>
                                                    </div>
                                                    <div className="space-y-3 sm:space-y-4">
                                                        {chat.messages.length === 0 ? (
                                                            <div className="italic text-gray-500 text-sm sm:text-base">No messages yet.</div>
                                                        ) : (
                                                            chat.messages.map((msg, i) => {
                                                                const isInvestor = msg.role !== 'entrepreneur';
                                                                const name = msg.username || (isInvestor ? investorName : 'Entrepreneur');
                                                                const initial = name.charAt(0).toUpperCase();
                                                                return (
                                                                    <div key={msg.id} className={`flex ${isInvestor ? 'justify-start' : 'justify-end'} items-end w-full`}>
                                                                        {isInvestor && (
                                                                            <div className="flex flex-col items-center mr-2">
                                                                                <div className="rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-white bg-navy text-xs sm:text-base">
                                                                                    {initial}
                                                                                </div>
                                                                                <div className="text-xs text-navy font-semibold mt-1 hidden sm:block">{name}</div>
                                                                            </div>
                                                                        )}
                                                                        <div className={`max-w-[80%] sm:max-w-[70%] px-3 sm:px-4 py-2 sm:py-3 rounded-xl shadow-sm ${isInvestor ? 'bg-gray-100 text-left' : 'bg-[#C2D7E2] text-right'} flex flex-col`}>
                                                                            <div className="text-sm sm:text-base text-navy break-words">{msg.message}</div>
                                                                            <div className="text-xs text-gray-500 mt-1">{dayjs(msg.timestamp).format('MMM D, YYYY h:mm A')}</div>
                                                                        </div>
                                                                        {!isInvestor && (
                                                                            <div className="flex flex-col items-center ml-2">
                                                                                <div className="rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-white bg-[#C2D7E2] text-navy text-xs sm:text-base">
                                                                                    {initial}
                                                                                </div>
                                                                                <div className="text-xs text-navy font-semibold mt-1 hidden sm:block">{name}</div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                                </div>
                            )}

                            {/* Q&A Section */}
                            {activeTab === 6 && (
                                <div className="bg-white/80 rounded-xl shadow p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8 w-full">
                                    <div className="space-y-8">
                                        <div className="flex items-center justify-between">
                                            <h2 className="text-[#8B1C2D] font-bold text-xl sm:text-2xl flex items-center">
                                                <i className="fas fa-question-circle mr-3 text-[#8B1C2D]"></i>
                                                QUESTIONS & ANSWERS
                                            </h2>
                                            <div className="bg-[#E5F0F7] px-3 py-1.5 rounded-full">
                                                <span className="text-navy font-semibold">
                                                    {campaignQuestions?.length ||
                                                        0}{" "}
                                                    Questions
                                                </span>
                                            </div>
                                        </div>

                                        {/* Ask a question form */}
                                        <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-6 border border-[#C2D7E2]/50">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="rounded-full bg-navy w-10 h-10 flex items-center justify-center flex-shrink-0">
                                                    <i className="fas fa-comment-dots text-white"></i>
                                                </div>
                                                <h3 className="text-navy font-semibold text-lg">
                                                    Have a question about this
                                                    campaign?
                                                </h3>
                                            </div>
                                            <form
                                                onSubmit={handleAskQuestion}
                                                className="space-y-4"
                                            >
                                                <div className="relative">
                                                    <textarea
                                                        className="w-full p-4 border-2 border-[#C2D7E2] rounded-xl focus:ring-2 focus:ring-navy focus:border-transparent resize-none bg-[#F5F7FA] shadow-inner"
                                                        rows="4"
                                                        placeholder="Type your question here..."
                                                        value={newQuestion}
                                                        onChange={(e) =>
                                                            setNewQuestion(
                                                                e.target.value,
                                                            )
                                                        }
                                                        required
                                                    ></textarea>
                                                    <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                                                        {newQuestion.length}/500
                                                    </div>
                                                </div>
                                                <div className="flex justify-end">
                                                    <button
                                                        type="submit"
                                                        className="bg-navy text-white py-2.5 px-8 rounded-lg hover:bg-[#003A61] transition duration-200 flex items-center shadow-md"
                                                    >
                                                        <i className="fas fa-paper-plane mr-2"></i>{" "}
                                                        Submit Question
                                                    </button>
                                                </div>
                                            </form>
                                        </div>

                                        {/* Questions list */}
                                        <div className="mt-6">
                                            <div className="flex items-center gap-2 mb-4">
                                                <div className="h-px bg-[#E5F0F7] flex-grow"></div>
                                                <h3 className="text-navy font-semibold px-4 py-1 bg-[#E5F0F7] rounded-full">
                                                    Questions
                                                </h3>
                                                <div className="h-px bg-[#E5F0F7] flex-grow"></div>
                                            </div>

                                            {loadingQuestions ? (
                                                <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-[#C2D7E2]/30">
                                                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#C2D7E2] border-t-navy"></div>
                                                    <p className="mt-4 text-navy font-medium">
                                                        Loading questions...
                                                    </p>
                                                </div>
                                            ) : campaignQuestions.length ===
                                              0 ? (
                                                <div className="bg-white rounded-xl p-8 shadow-sm border border-[#C2D7E2]/30 text-center">
                                                    <div className="rounded-full bg-[#E5F0F7] w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                                        <i className="fas fa-comments text-navy text-2xl"></i>
                                                    </div>
                                                    <h4 className="text-navy font-semibold text-lg mb-2">
                                                        No Questions Yet
                                                    </h4>
                                                    <p className="text-gray-600 max-w-md mx-auto mb-4">
                                                        No questions have been
                                                        asked yet about this
                                                        campaign. Be the first
                                                        to ask!
                                                    </p>
                                                    <button
                                                        onClick={() =>
                                                            document
                                                                .querySelector(
                                                                    "textarea",
                                                                )
                                                                .focus()
                                                        }
                                                        className="text-navy border-2 border-navy px-4 py-2 rounded-lg hover:bg-[#E5F0F7] transition duration-200"
                                                    >
                                                        Ask a Question
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="space-y-6">
                                                    {campaignQuestions.map(
                                                        (question) => {
                                                            const replyCount =
                                                                (question
                                                                    .replies
                                                                    ?.length ||
                                                                    0) +
                                                                (question.answer
                                                                    ? 1
                                                                    : 0);
                                                            const isThreadReplying =
                                                                selectedQuestionId ===
                                                                    question.id &&
                                                                activeReplyKey ===
                                                                    "thread";
                                                            return (
                                                                <div
                                                                    key={
                                                                        question.id
                                                                    }
                                                                    className="bg-white rounded-2xl border border-[#e3eaf3] overflow-hidden hover:shadow-md transition-shadow duration-300"
                                                                >
                                                                    {/* ── Original Question ── */}
                                                                    <div className="p-5 sm:p-6">
                                                                        <div className="flex items-start gap-3">
                                                                            {question.asker_first_name ? (
                                                                                <div className="rounded-full w-9 h-9 flex items-center justify-center font-bold text-white text-sm bg-navy flex-shrink-0">
                                                                                    {question.asker_first_name
                                                                                        .charAt(
                                                                                            0,
                                                                                        )
                                                                                        .toUpperCase()}
                                                                                </div>
                                                                            ) : (
                                                                                <div className="rounded-full w-9 h-9 flex items-center justify-center text-sm text-navy bg-[#E5F0F7] flex-shrink-0">
                                                                                    <i className="fas fa-user"></i>
                                                                                </div>
                                                                            )}
                                                                            <div className="flex-1 min-w-0">
                                                                                <div className="flex flex-wrap items-center gap-2 mb-0.5">
                                                                                    <span className="font-semibold text-sm text-[#23304a]">
                                                                                        {question.asker_first_name
                                                                                            ? `${question.asker_first_name} ${question.asker_last_name}`
                                                                                            : "Anonymous"}
                                                                                    </span>
                                                                                    <span className="text-xs text-[#6b7a8f]">
                                                                                        ·{" "}
                                                                                        {dayjs(
                                                                                            question.audit_created_on,
                                                                                        ).format(
                                                                                            "MMM D, YYYY · h:mm A",
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                                <div
                                                                                    className="text-sm text-gray-700 mt-2 leading-relaxed"
                                                                                    dangerouslySetInnerHTML={{
                                                                                        __html: question.question,
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* ── Official Answer (Campaign Owner / Admin) ── */}
                                                                    {question.answer && (
                                                                        <div className="mx-5 sm:mx-6 mb-4 pl-4 border-l-4 border-[#8B1C2D]/50 bg-[#fdf8f8] rounded-r-xl p-4">
                                                                            <div className="flex items-start gap-3">
                                                                                <div className="rounded-full w-8 h-8 flex items-center justify-center font-bold text-white text-xs bg-[#8B1C2D] flex-shrink-0">
                                                                                    {question.answerer_first_name ? (
                                                                                        question.answerer_first_name
                                                                                            .charAt(
                                                                                                0,
                                                                                            )
                                                                                            .toUpperCase()
                                                                                    ) : (
                                                                                        <i className="fas fa-building text-xs"></i>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex-1 min-w-0">
                                                                                    <div className="flex flex-wrap items-center gap-2 mb-0.5">
                                                                                        <span className="font-semibold text-sm text-[#8B1C2D]">
                                                                                            {question.answerer_first_name
                                                                                                ? `${question.answerer_first_name} ${question.answerer_last_name}`
                                                                                                : `${campaign.company_name || "Campaign"} Team`}
                                                                                        </span>
                                                                                        <span className="bg-[#F7E5E8] text-[#8B1C2D] text-xs font-medium px-2 py-0.5 rounded-full">
                                                                                            Campaign
                                                                                            Owner
                                                                                        </span>
                                                                                        <span className="text-xs text-[#6b7a8f]">
                                                                                            ·{" "}
                                                                                            {dayjs(
                                                                                                question.audit_updated_on,
                                                                                            ).format(
                                                                                                "MMM D, YYYY",
                                                                                            )}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div
                                                                                        className="text-sm text-gray-700 mt-2 leading-relaxed"
                                                                                        dangerouslySetInnerHTML={{
                                                                                            __html: question.answer,
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* ── Replies Thread (each reply nested under who they replied to) ── */}
                                                                    {Array.isArray(
                                                                        question.replies,
                                                                    ) &&
                                                                        question
                                                                            .replies
                                                                            .length >
                                                                            0 && (
                                                                            <div className="mx-5 sm:mx-6 mb-2 pl-4 border-l-2 border-[#e3eaf3] space-y-3">
                                                                                {renderRepliesTree(
                                                                                    question.replies,
                                                                                    question,
                                                                                )}
                                                                            </div>
                                                                        )}

                                                                    {/* ── Thread-level inline reply form (from action bar) ── */}
                                                                    {isThreadReplying && (
                                                                        <div className="mx-5 sm:mx-6 mb-4 pl-4 border-l-2 border-navy/20">
                                                                            <div className="flex items-start gap-2">
                                                                                <div
                                                                                    className={`rounded-full w-8 h-8 flex items-center justify-center font-bold text-white text-xs flex-shrink-0 ${user?.role === "admin" ? "bg-[#8B1C2D]" : "bg-navy"}`}
                                                                                >
                                                                                    {user?.first_name ? (
                                                                                        user.first_name
                                                                                            .charAt(
                                                                                                0,
                                                                                            )
                                                                                            .toUpperCase()
                                                                                    ) : (
                                                                                        <i className="fas fa-user text-xs"></i>
                                                                                    )}
                                                                                </div>
                                                                                <form
                                                                                    onSubmit={
                                                                                        handleReplySubmit
                                                                                    }
                                                                                    className="flex-1"
                                                                                >
                                                                                    <textarea
                                                                                        value={
                                                                                            replyText
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            setReplyText(
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                        }
                                                                                        className="w-full px-3 py-2.5 text-sm border-2 border-[#C2D7E2] rounded-xl focus:ring-2 focus:ring-navy focus:border-transparent resize-none bg-[#F5F7FA]"
                                                                                        rows={
                                                                                            3
                                                                                        }
                                                                                        placeholder={
                                                                                            user?.role ===
                                                                                            "admin"
                                                                                                ? "Reply as Admin…"
                                                                                                : "Write a reply…"
                                                                                        }
                                                                                        autoFocus
                                                                                        required
                                                                                    />
                                                                                    <div className="flex items-center justify-between mt-2">
                                                                                        {user?.role ===
                                                                                        "admin" ? (
                                                                                            <span className="text-xs text-[#8B1C2D] font-semibold flex items-center gap-1">
                                                                                                <i className="fas fa-shield-alt"></i>{" "}
                                                                                                Replying
                                                                                                as
                                                                                                Admin
                                                                                            </span>
                                                                                        ) : (
                                                                                            <span />
                                                                                        )}
                                                                                        <div className="flex items-center gap-2">
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() =>
                                                                                                    openReply(
                                                                                                        question.id,
                                                                                                        "thread",
                                                                                                    )
                                                                                                }
                                                                                                className="text-xs text-[#6b7a8f] px-3 py-1.5 rounded-lg hover:bg-[#e3eaf3] transition-colors font-medium"
                                                                                            >
                                                                                                Cancel
                                                                                            </button>
                                                                                            <button
                                                                                                type="submit"
                                                                                                disabled={
                                                                                                    submittingReply
                                                                                                }
                                                                                                className="bg-navy text-white text-xs px-4 py-1.5 rounded-lg hover:bg-[#003A61] transition-colors flex items-center gap-1.5 font-medium disabled:opacity-60"
                                                                                            >
                                                                                                {submittingReply ? (
                                                                                                    <>
                                                                                                        <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>{" "}
                                                                                                        Posting…
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <>
                                                                                                        <i className="fas fa-paper-plane"></i>{" "}
                                                                                                        Reply
                                                                                                    </>
                                                                                                )}
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                </form>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* ── Action Bar ── */}
                                                                    <div className="px-5 sm:px-6 py-3 border-t border-[#e3eaf3] bg-[#F5F7FA] flex items-center justify-between">
                                                                        <div className="flex items-center gap-2 text-xs text-[#6b7a8f]">
                                                                            <i className="fas fa-comment-alt"></i>
                                                                            <span>
                                                                                {replyCount >
                                                                                0
                                                                                    ? `${replyCount} comment${replyCount !== 1 ? "s" : ""}`
                                                                                    : "No comments yet"}
                                                                            </span>
                                                                            {!question.answer && (
                                                                                <span className="ml-1 bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full font-medium border border-amber-100">
                                                                                    Awaiting
                                                                                    official
                                                                                    reply
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <button
                                                                            onClick={() =>
                                                                                openReply(
                                                                                    question.id,
                                                                                    "thread",
                                                                                )
                                                                            }
                                                                            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                                                                                isThreadReplying
                                                                                    ? "bg-[#e3eaf3] text-navy"
                                                                                    : "text-[#6b7a8f] hover:bg-[#e3eaf3] hover:text-navy"
                                                                            }`}
                                                                        >
                                                                            <i className="fas fa-reply"></i>
                                                                            {isThreadReplying
                                                                                ? "Cancel Reply"
                                                                                : "Reply"}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        },
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* RIGHT COLUMN */}
                    <div className="w-full lg:w-1/3 xl:w-3/10 2xl:w-1/4 flex flex-col gap-3 sm:gap-4">
                        {/* Campaign Name */}
                        <div className="bg-white/80 rounded-xl shadow p-4 sm:p-6 mb-2 w-full">
                            <div className="mb-4">
                                <span className="text-navy font-semibold block mb-1 text-sm sm:text-base">
                                    <i className="fas fa-flag mr-2"></i>CAMPAIGN
                                    NAME
                                </span>
                                <span className="block text-base sm:text-lg text-navy font-bold break-words">
                                    {campaign.campaign_name || "—"}
                                </span>
                            </div>
                            <div>
                                <span className="text-navy font-semibold block mb-1 text-sm sm:text-base">
                                    <i className="fas fa-align-left mr-2"></i>
                                    CAMPAIGN DESCRIPTION
                                </span>
                                <span className="block text-sm sm:text-base text-navy whitespace-pre-line break-words">
                                    {campaign.campaign_description || "—"}
                                </span>
                            </div>
                        </div>
                        <div className="w-full overflow-x-hidden rounded-2xl border border-[#dfe5e8] bg-white p-4 shadow-lg lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto sm:p-5">
                        {/* Progress Bar */}{" "}
                        <div className="flex flex-col items-center w-full mt-3 sm:mt-4 mb-6 sm:mb-8">
                            {/* Progress bar visualization */}
                            <div className="flex items-center w-full relative">
                                {/* Progress bar background */}
                                <div className="absolute top-1/2 left-0 right-0 h-1.5 sm:h-2 bg-gray-200 -translate-y-1/2 rounded-full"></div>

                                {/* Dynamic progress fill based on investment percentage */}
                                {(() => {
                                    // Calculate progress percentage
                                    const fundGoal = Number(
                                        campaign.fund_raising_goal || 0,
                                    );
                                    const raised = Number(totalInvestment || 0);
                                    const percentage =
                                        fundGoal > 0
                                            ? Math.min(
                                                  100,
                                                  (raised / fundGoal) * 100,
                                              )
                                            : 0;

                                    return (
                                        <div
                                            className="absolute top-1/2 left-0 h-1.5 sm:h-2 bg-gradient-to-r from-navy to-[#C2D7E2] -translate-y-1/2 transition-all duration-1000 rounded-full shadow-sm"
                                            style={{ width: `${percentage}%` }}
                                        ></div>
                                    );
                                })()}

                                {/* Milestone circles */}
                                <div className="flex-1 flex items-center z-10">
                                    <div className="flex flex-col items-center">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-full border-2 sm:border-4 border-navy bg-navy flex items-center justify-center text-white drop-shadow-md transform hover:scale-105 transition-all">
                                            <i className="fas fa-paper-plane text-sm sm:text-lg lg:text-xl"></i>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex-1 flex items-center justify-center z-10">
                                    <div className="flex flex-col items-center">
                                        {(() => {
                                            const fundGoal = Number(
                                                campaign.fund_raising_goal || 0,
                                            );
                                            const raised = Number(
                                                totalInvestment || 0,
                                            );
                                            const percentage =
                                                fundGoal > 0
                                                    ? (raised / fundGoal) * 100
                                                    : 0;
                                            const reachedHalf =
                                                percentage >= 50;

                                            return (
                                                <div
                                                    className={`w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-full border-2 sm:border-4 transition-all duration-500
                                                    ${
                                                        reachedHalf
                                                            ? "border-navy bg-navy text-white scale-105 drop-shadow-md"
                                                            : "border-[#C2D7E2] bg-white text-[#C2D7E2]"
                                                    } flex items-center justify-center`}
                                                >
                                                    <i
                                                        className={`fas fa-check text-sm sm:text-lg lg:text-xl ${
                                                            reachedHalf
                                                                ? "animate-pulse"
                                                                : ""
                                                        }`}
                                                    ></i>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                                <div className="flex-1 flex items-center justify-end z-10">
                                    <div className="flex flex-col items-center">
                                        {(() => {
                                            const fundGoal = Number(
                                                campaign.fund_raising_goal || 0,
                                            );
                                            const raised = Number(
                                                totalInvestment || 0,
                                            );
                                            const percentage =
                                                fundGoal > 0
                                                    ? (raised / fundGoal) * 100
                                                    : 0;
                                            const reachedGoal =
                                                percentage >= 100;

                                            return (
                                                <div
                                                    className={`w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-full border-2 sm:border-4 transition-all duration-500
                                                    ${
                                                        reachedGoal
                                                            ? "border-navy bg-navy text-white scale-105 drop-shadow-md"
                                                            : "border-[#C2D7E2] bg-white text-[#C2D7E2]"
                                                    } flex items-center justify-center`}
                                                >
                                                    <i
                                                        className={`fas fa-star text-sm sm:text-lg lg:text-xl ${
                                                            reachedGoal
                                                                ? "animate-pulse"
                                                                : ""
                                                        }`}
                                                    ></i>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                            {/* Milestone labels with improved alignment */}
                            <div className="flex w-full mt-2 sm:mt-3 text-navy font-bold">
                                <div className="flex-1 flex justify-start">
                                    <div
                                        className="text-center"
                                        style={{ width: "40px" }}
                                    >
                                        <div className="text-xs sm:text-sm lg:text-base font-semibold leading-tight">
                                            Launched
                                        </div>
                                        <div className="font-medium text-xs sm:text-sm text-[#507996] mt-0.5">
                                            {campaign.approved_at
                                                ? dayjs(
                                                      campaign.approved_at,
                                                  ).format("MMM D, YYYY")
                                                : "[DATE]"}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex-1 flex justify-center">
                                    <div
                                        className="text-center"
                                        style={{ width: "40px" }}
                                    >
                                        <div className="text-xs sm:text-sm lg:text-base font-semibold leading-tight">
                                            Minimum
                                            <br className="hidden sm:block" />
                                            <span className="sm:hidden"> </span>
                                            Target
                                        </div>
                                        <div className="font-medium text-xs sm:text-sm text-[#507996] mt-0.5">
                                            $
                                            {Number(
                                                campaign.minimum_raised,
                                            ).toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex-1 flex justify-end">
                                    <div
                                        className="text-center"
                                        style={{ width: "40px" }}
                                    >
                                        <div className="text-xs sm:text-sm lg:text-base font-semibold leading-tight">
                                            Goal
                                            <br className="hidden sm:block" />
                                            <span className="sm:hidden"> </span>
                                            Hit!
                                        </div>
                                        <div className="font-medium text-xs sm:text-sm text-[#507996] mt-0.5">
                                            $
                                            {Number(
                                                campaign.fund_raising_goal || 0,
                                            ).toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Current progress indicator - improved design */}
                            <div className="mt-5 text-center">
                                {(() => {
                                    const fundGoal = Number(
                                        campaign.fund_raising_goal || 0,
                                    );
                                    const raised = Number(totalInvestment || 0);
                                    const percentage =
                                        fundGoal > 0
                                            ? Math.min(
                                                  100,
                                                  (raised / fundGoal) * 100,
                                              )
                                            : 0;
                                    const percentageRounded =
                                        percentage < 1 && percentage > 0
                                            ? percentage.toFixed(2)
                                            : Math.round(percentage);
                                    // Determine color classes based on progress
                                    let progressColor =
                                        "from-navy to-[#C2D7E2]";
                                    if (percentage >= 100) {
                                        progressColor =
                                            "from-navy to-[#C2D7E2]";
                                    } else if (percentage >= 50) {
                                        progressColor =
                                            "from-navy to-[#C2D7E2]";
                                    }

                                    return (
                                        <div className="inline-flex items-center bg-white rounded-full px-4 py-2 shadow-md border border-gray-100">
                                            <div className="w-24 bg-gray-100 rounded-full h-3 mr-3 overflow-hidden">
                                                <div
                                                    className={`bg-gradient-to-r ${progressColor} h-3 rounded-full transition-all duration-1000`}
                                                    style={{
                                                        width: `${percentage}%`,
                                                    }}
                                                ></div>
                                            </div>
                                            <span className="text-navy font-bold mr-1">
                                                {percentageRounded}%
                                            </span>
                                            <span className="text-[#507996] text-sm">
                                                Complete
                                            </span>
                                        </div>
                                    );
                                })()}
                                {/* Minimum investment amount under progress */}
                                <div className="mt-3 text-center w-full">
                                    <div className="text-sm text-navy font-semibold">
                                        Minimum investment
                                    </div>
                                    <div className="text-sm font-semibold text-navy">
                                        {(() => {
                                            const minVal =
                                                campaign.investment_minimum ??
                                                campaign.investment_min ??
                                                null;
                                            return minVal != null && minVal !== ""
                                                ? `$${Number(minVal).toLocaleString()}`
                                                : "—";
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Raised/To Go */}
                        <div className="mt-3 sm:mt-4 w-full">
                            <div className="flex flex-col sm:flex-row justify-between items-center mt-2 mb-1 w-full bg-white/80 rounded-lg shadow-sm p-3 sm:p-4 gap-3 sm:gap-0">
                                <div className="flex-1 w-full sm:w-auto">
                                    <div className="flex items-center">
                                        <div className="rounded-full bg-[#E5F0F7] p-2 sm:p-3 mr-2 sm:mr-3">
                                            <i className="fas fa-dollar-sign text-navy text-sm sm:text-base"></i>
                                        </div>
                                        <div>
                                            <p className="text-base sm:text-lg font-bold font-poppins text-navy">
                                                $
                                                {totalInvestment
                                                    ? Number(
                                                          totalInvestment,
                                                      ).toLocaleString()
                                                    : "0"}
                                            </p>
                                            <p className="text-xs sm:text-sm text-[#507996]">
                                                Total Raised
                                            </p>
                                        </div>
                                    </div>
                                    {campaign.total_investors !== undefined && (
                                        <span className="text-navy text-xs italic block mt-2 ml-8 sm:ml-11">
                                            from {campaign.total_investors}{" "}
                                            investor
                                            {campaign.total_investors !== 1
                                                ? "s"
                                                : ""}
                                        </span>
                                    )}
                                </div>

                                <div className="flex-1 flex justify-end w-full sm:w-auto">
                                    <div className="flex items-center">
                                        <div className="rounded-full bg-[#F7E5E8] p-2 sm:p-3 mr-2 sm:mr-3">
                                            <i className="fas fa-chart-line text-[#8B1C2D] text-sm sm:text-base"></i>
                                        </div>
                                        <div>
                                            <p className="text-base sm:text-lg font-bold font-poppins text-[#8B1C2D]">
                                                $
                                                {Number(
                                                    (campaign.fund_raising_goal ||
                                                        0) -
                                                        (totalInvestment || 0),
                                                ).toLocaleString()}
                                            </p>
                                            <p className="text-xs sm:text-sm text-[#8B1C2D]/70">
                                                To Goal
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Investment Block */}
                            <div ref={investmentTermsRef} className="relative mt-4 rounded-xl border border-navy/15 bg-[#F5F7FA] p-4">
                                <h3 className="mb-3 font-bold text-navy">Investment Terms</h3>
                                <dl className="grid grid-cols-2 gap-3">
                                    <div>
                                        <dt className="text-xs font-semibold uppercase tracking-wide text-[#6b7a8f]">Valuation</dt>
                                        <dd className="mt-1 font-bold text-[#23304a]">
                                            {campaign.legal_uploads?.investment_valuation || campaign.valuation || "N/A"}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-xs font-semibold uppercase tracking-wide text-[#6b7a8f]">Price per share</dt>
                                        <dd className="mt-1 font-bold text-[#23304a]">
                                            {campaign.unit_price != null && campaign.unit_price !== ""
                                                ? `$${Number(campaign.unit_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                : "N/A"}
                                        </dd>
                                    </div>
                                </dl>
                                {campaignPerks.length > 0 ? (
                                    <button
                                        type="button"
                                        ref={perksTriggerRef}
                                        onClick={() => {
                                            if (perksPanelOpen) {
                                                closePerksPopup();
                                            } else {
                                                setShowAllPerks(false);
                                                setPerksPanelOpen(true);
                                            }
                                        }}
                                        aria-haspopup="dialog"
                                        aria-expanded={perksPanelOpen}
                                        aria-controls="investor-perks-panel"
                                        className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-navy/20 bg-white px-3 py-2 text-sm font-semibold text-navy transition hover:border-navy hover:bg-[#f0f6fb]"
                                    >
                                        <i className="fas fa-gift"></i>
                                        {perksPanelOpen ? "Hide Investor Perks" : "View Investor Perks"}
                                    </button>
                                ) : (
                                    <p className="mt-4 text-sm italic text-gray-500">No investor perks are currently offered.</p>
                                )}
                                {perksPanelOpen && (
                                    <dialog
                                        ref={perksDialogRef}
                                        id="investor-perks-panel"
                                        className="aqi-investor-perks-dialog fixed m-0 overflow-y-auto rounded-xl border border-[#c9a227]/30 bg-white p-0 shadow-2xl"
                                        aria-modal="true"
                                        aria-labelledby="investor-perks-title"
                                        onCancel={(event) => {
                                            event.preventDefault();
                                            closePerksPopup();
                                        }}
                                        onClick={(event) => {
                                            if (event.target !== event.currentTarget) return;
                                            const rect = event.currentTarget.getBoundingClientRect();
                                            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
                                                closePerksPopup();
                                            }
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === "Escape") {
                                                event.stopPropagation();
                                                closePerksPopup();
                                            }
                                        }}
                                    >
                                        <div className="relative p-4">
                                            <button
                                                type="button"
                                                autoFocus
                                                aria-label="Close investor perks"
                                                onClick={closePerksPopup}
                                                className="absolute right-4 top-3 text-2xl font-bold text-gray-400 hover:text-[#8B1C2D]"
                                            >
                                                ×
                                            </button>
                                            <h2 id="investor-perks-title" className="pr-8 text-xl font-bold text-[#23304a]">
                                                <i className="fas fa-gift mr-2 text-navy"></i>
                                                Earn perks when you invest
                                            </h2>
                                            <div className="mt-5 space-y-5">
                                                {campaignPerks
                                                    .slice(0, showAllPerks ? undefined : 1)
                                                    .map((perk, idx) => (
                                                    <div key={idx}>
                                                        {perk.amount && (
                                                            <span className="inline-flex rounded bg-navy px-3 py-1 text-sm font-bold text-white">
                                                                {formatInvestorPerkAmount(perk.amount)}
                                                            </span>
                                                        )}
                                                        {perk.description && (
                                                            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-gray-600">
                                                                {perk.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                            {campaignPerks.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowAllPerks((expanded) => !expanded)}
                                                    aria-expanded={showAllPerks}
                                                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg border border-navy/20 px-3 py-2 text-sm font-semibold text-navy transition hover:border-navy hover:bg-[#f0f6fb]"
                                                >
                                                    {showAllPerks
                                                        ? "Show less"
                                                        : `See all ${campaignPerks.length} perks`}
                                                    <i className={`fas fa-chevron-${showAllPerks ? "up" : "down"} text-xs`}></i>
                                                </button>
                                            )}
                                        </div>
                                    </dialog>
                                )}
                            </div>
                            {/* Completed and unsuccessful campaigns no longer accept investments. */}
                            {isCampaignEnded(campaign) ? (
                                <div className="text-center bg-gradient-to-r from-[#F7E5E8] to-[#FFE5E5] rounded-lg p-3 sm:p-4 border border-[#8B1C2D]/20 mt-3 sm:mt-4 shadow-sm">
                                    <div className="text-[#8B1C2D] font-bold text-base sm:text-lg mb-2 flex items-center justify-center">
                                        <div className="p-1 sm:p-2 mr-1 sm:mr-2">
                                            <i className="fas fa-clock text-[#8B1C2D] text-sm sm:text-base"></i>
                                        </div>
                                        {campaign.status === "completed"
                                            ? "Campaign Completed"
                                            : campaign.status === "closed"
                                              ? "Campaign Closed"
                                              : "Campaign Ended"}
                                    </div>
                                    <p className="text-navy font-semibold text-sm sm:text-base">
                                        {campaign.status === "completed"
                                            ? "This campaign successfully reached its minimum target and is no longer accepting investments."
                                            : campaign.status === "closed"
                                              ? "This campaign closed without reaching its minimum target and is no longer accepting investments."
                                            : "This investment campaign has ended and is no longer accepting investments."}
                                    </p>
                                    <div className="mt-2 sm:mt-3 text-xs sm:text-sm text-[#507996]">
                                        <i className="fas fa-calendar-alt mr-1"></i>{" "}
                                        {campaign.status === "completed"
                                            ? "Completed: "
                                            : campaign.status === "closed"
                                              ? "Closed: "
                                              : "Ended: "}
                                        {getCampaignDeadline(campaign)?.format(
                                            "MMMM D, YYYY",
                                        )}
                                    </div>
                                </div>
                            ) : !user ? (
                                <div className="my-6 text-center font-poppins text-base font-semibold leading-relaxed tracking-wide text-[#8B1C2D] sm:my-8 sm:text-lg">
                                    Please{" "}
                                    <button
                                        className="underline text-navy hover:text-[#8B1C2D] ml-1"
                                        onClick={() => {
                                            localStorage.setItem(
                                                "redirectAfterLogin",
                                                window.location.pathname,
                                            );
                                            localStorage.setItem(
                                                "redirectSource",
                                                "campaign",
                                            );
                                            navigate("/login");
                                        }}
                                    >
                                        login
                                    </button>{" "}
                                    to invest in this campaign
                                </div>
                            ) : user.role === "investor" ? (
                                <>
                                    <button
                                        onClick={handleInvestNowClick}
                                        className="w-3/4 sm:w-1/2 mx-auto block bg-navy text-white py-2 rounded-2xl text-base sm:text-lg font-semibold mt-4"
                                    >
                                        Invest Now
                                    </button>
                                    <div className="mt-3 sm:mt-4 text-center">
                                        <p className="text-navy text-xs sm:text-sm font-poppins font-medium mb-2 leading-relaxed px-2">
                                            By Continuing, you agree to the{" "}
                                            <a
                                                href="/docs/terms_of_use_10_2026.pdf"
                                                className="underline text-navy"
                                                target="_blank"
                                            >
                                                Terms and Conditions
                                            </a>
                                            ,{" "}
                                            <a
                                                href="/docs/privacy_policy_10_2026.pdf"
                                                className="underline text-navy"
                                                target="_blank"
                                            >
                                                Privacy Policy
                                            </a>
                                            ,{" "}
                                            <a
                                                href="/docs/investor_agreement_05_2026.pdf"
                                                className="underline text-navy"
                                                target="_blank"
                                            >
                                                Investor Agreement
                                            </a>
                                            ,{" "}
                                            <a
                                                href="/docs/electronic_delivery_of_documents_11_2025.pdf"
                                                className="underline text-navy"
                                                target="_blank"
                                            >
                                                Electronic Delivery of Documents
                                            </a>
                                            , and{" "}
                                            <a
                                                href="/docs/electronic_funds_transfers_03_2026.pdf"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-navy underline hover:text-navy-2"
                                            >
                                                Electronic Funds Transfers
                                            </a>
                                        </p>
                                    </div>
                                </>
                            ) : null}
                        </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />

            {/* Investment Calculator Modal */}
            {investmentCalculatorModalOpen && (
                <InvestmentCalculatorModal
                    isOpen={investmentCalculatorModalOpen}
                    onClose={() => setInvestmentCalculatorModalOpen(false)}
                    investorData={investorData}
                    campaignId={campaignId}
                    onProceed={handleCalculatorProceed}
                />
            )}
            {/* EFT Agreement Modal */}
            {eftModalOpen && (
                <EFTAgreementModal
                    isOpen={eftModalOpen}
                    onAgree={handleEFTAgree}
                    onAgreementOpen={handleSubscriptionAgreementOpen}
                    onClose={() => {
                        setEftModalOpen(false);
                        setAgreementAcceptanceToken(null);
                    }}
                    subscriptionAgreement={
                        campaign.legal_uploads?.subscription_agreement?.[0] ||
                        null
                    }
                    defaultSignerName={
                        [user?.first_name, user?.last_name]
                            .filter(Boolean)
                            .join(" ")
                    }
                />
            )}

            {modalOpen && (activePdf || activeImage) && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                    {/* Modal Content */}
                    <div className="relative w-full max-w-6xl bg-white p-3 sm:p-4 lg:p-6 rounded-xl shadow-lg max-h-[95vh] overflow-hidden">
                        {/* Close Button */}
                        <div className="flex justify-end mb-2">
                            <button
                                className="text-gray-600 hover:text-red-500 text-xl sm:text-2xl font-bold focus:outline-none"
                                onClick={() => {
                                    setModalOpen(false);
                                    setActivePdf(null);
                                    setActiveImage(null);
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        {/* PDF or Image Display */}
                        {activePdf ? (
                            <iframe
                                src={activePdf}
                                title="Full PDF View"
                                className="w-full h-[70vh] sm:h-[80vh] border border-gray-300 rounded-lg"
                            />
                        ) : (
                            <img
                                src={activeImage}
                                alt="Preview"
                                className="w-full max-h-[70vh] sm:max-h-[80vh] object-contain rounded-lg border border-gray-300"
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

CampaignViewPage.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        role: PropTypes.string,
    }),
};

export default CampaignViewPage;
